/**
 * Registry-driven Payments connection tab (DNKADM-0110).
 *
 * The provider picker, credentials, webhook instructions, currency, and
 * environment controls are rendered from the v2 registry. The state machine is
 * driven by the connection descriptor, and every action re-fetches the descriptor
 * before the UI presents the next state.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  type AlertColor,
  AlertTitle,
  Box,
  Button,
  Chip,
  type ChipProps,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/errors';
import { listLocations, type V2Location } from '@/api/locations';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import {
  convertPaymentsConnectionToManual,
  deletePaymentsConnection,
  disconnectPaymentsOAuth,
  getPaymentsAttention,
  getPaymentsConnection,
  getPaymentsRegistry,
  getPaymentsStatus,
  getWebhookStatus,
  getPspLocations,
  putPaymentsConnection,
  sendWebhookTest,
  setPspLocation,
  setUpApplePay,
  startConnectSession,
  validatePaymentsConnection,
  webhookStatusReason,
  type PaymentsApplePayDomain,
  type PaymentsApplePayStatus,
  type PaymentsAttention,
  type PaymentsConnectionDescriptor,
  type PaymentsConnectionInput,
  type PaymentsCurrencyBand,
  type PaymentsFieldDescriptor,
  type PaymentsPspLocation,
  type PaymentsProviderRegistry,
  type PaymentsRegistry,
  type PaymentsStatus,
  type PaymentsWebhookStatus,
  type PaymentsWebhookTestResult,
} from '@/api/paymentsV2';
import { useToast } from '@/components/useToast';
import { Icon, inkAlpha, MONO, resolveTokens } from '@/theme';
import { resolvePaymentsMaps } from '@/theme/maps/payments';
import { scrubBrand } from '@/utils/whitelabel';
import { PaymentsConnectUrlError, validatePaymentsConnectUrl } from './paymentsConnectNavigation';
import { ScreenError } from '@/components/ScreenError';
import { actionErrorMessage } from '@/api/screenError';
import { isForbiddenError } from '@/components/accessCopy';
import { useCalendarZone } from '@/location/useCalendarZone';
import { formatInstantMs, instantMs } from '@/utils/instantFormat';

const TENANT_DEFAULT = '__tenant-default__';
const CONNECT_CONTEXT_KEY = 'dink-admin.payments.connectContext';
const SECRET_PLACEHOLDER = '•••• (saved)';
const OVERRIDE_PIN_KEY = 'overridePin';
const SANDBOX_ENVIRONMENT_LABEL = 'Sandbox — demo / test payments only';
const SQUARE_DASHBOARD_URL = 'https://app.squareup.com/dashboard';
const PROVIDER_LOCATION_ANCHOR_ID = 'payments-provider-location';

interface ConnectContext {
  locationRef?: string;
  provider: string;
  environment: string;
}

interface ConnectOAuthErrorView {
  message: string;
  retryable: boolean;
}

type LoadState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: unknown };

type ValidationErrors = Record<string, string>;

interface CurrencyBandFormRow {
  currency: string;
  minMajor: string;
  maxMajor: string;
}

interface ProviderSummaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  color?: 'primary' | 'error' | 'inherit';
  variant?: 'contained' | 'outlined' | 'text';
  tooltip?: string;
}

interface ActivationReadiness {
  requiresValidation: boolean;
  guardsAvailable: boolean;
  missingCurrencyBands: boolean;
  missingOverridePin: boolean;
  activationBlocked: boolean;
}

function loading<T>(): LoadState<T> {
  return { status: 'loading', data: null, error: null };
}

function ready<T>(data: T): LoadState<T> {
  return { status: 'ready', data, error: null };
}

function failed<T>(error: unknown): LoadState<T> {
  return { status: 'error', data: null, error };
}

function requestSuffix(err: unknown): string {
  return err instanceof ApiError && err.requestId ? ` (Request ${err.requestId})` : '';
}

function errorMessage(err: unknown, fallback: string): string {
  return `${serverMessage(err) ?? fallback}${requestSuffix(err)}`;
}

function selectedLocationRef(value: string): string | undefined {
  return value === TENANT_DEFAULT ? undefined : value;
}

function selectValue(locationRef: string | undefined): string {
  return locationRef ?? TENANT_DEFAULT;
}

function defaultLocationName(locations: V2Location[], id: string): string {
  const loc = locations.find((entry) => entry.id === id);
  return loc?.name && loc.name.length > 0 ? loc.name : id;
}

function isSecretField(field: PaymentsFieldDescriptor): boolean {
  return field.type === 'password';
}

function secretPresence(descriptor: PaymentsConnectionDescriptor, key: string): boolean {
  return descriptor[`${key}Set`] === true;
}

function providerLabel(provider: PaymentsProviderRegistry | null, providerId: string | null): string {
  return provider?.label ?? providerId ?? 'provider';
}

function connectionField(provider: PaymentsProviderRegistry, key: string): PaymentsFieldDescriptor | null {
  return provider.connectionFields.find((field) => field.key === key) ?? null;
}

function overridePinDescriptor(provider: PaymentsProviderRegistry): PaymentsFieldDescriptor | null {
  return (
    provider.overrideFields[0] ??
    connectionField(provider, OVERRIDE_PIN_KEY) ??
    provider.connectionFields.find((field) => {
      const key = field.key.toLowerCase();
      return field.type === 'password' && key.includes('override') && key.includes('pin');
    }) ??
    null
  );
}

function firstOption(field: PaymentsFieldDescriptor | null): string {
  return field?.options[0]?.value ?? '';
}

function resolvedEnvironment(
  value: string | null | undefined,
  field: PaymentsFieldDescriptor | null,
  permitted: boolean | null = null,
): string {
  const offered = field?.options.map((option) => option.value) ?? [];
  if (
    value &&
    (permitted === true ||
      (permitted === null && (offered.length === 0 || offered.includes(value))))
  ) {
    return value;
  }
  return firstOption(field) || 'production';
}

function environmentIsMismatch(
  value: string | null | undefined,
  field: PaymentsFieldDescriptor | null,
  permitted: boolean | null,
): boolean {
  if (value === null || value === undefined || value.length === 0) return false;
  if (permitted !== null) return !permitted;
  const offered = field?.options.map((option) => option.value) ?? [];
  return offered.length > 0 && !offered.includes(value);
}

function environmentIsChoice(provider: PaymentsProviderRegistry): boolean {
  return (connectionField(provider, 'environment')?.options.length ?? 0) >= 2;
}

function environmentLabel(value: string | null): string | null {
  if (value === null) return null;
  return value === 'sandbox' ? SANDBOX_ENVIRONMENT_LABEL : scrubBrand(value);
}

function currencyOptions(provider: PaymentsProviderRegistry): PaymentsFieldOptionLike[] {
  if (provider.multiCurrency) {
    const source =
      provider.allowedCurrencyOptions.length > 0
        ? provider.allowedCurrencyOptions
        : (connectionField(provider, 'currency')?.options.map((option) => option.value) ?? []);
    return source.map((value) => ({ value, label: value }));
  }
  return connectionField(provider, 'currency')?.options ?? [];
}

function providerRequiresBands(provider: PaymentsProviderRegistry): boolean {
  return provider.multiCurrency || provider.currencyBandFields.length > 0;
}

interface PaymentsFieldOptionLike {
  value: string;
  label: string;
}

function providerTemplate(
  descriptor: PaymentsConnectionDescriptor | null,
  providerId: string | null,
): string | null {
  if (descriptor === null || providerId === null) return null;
  const template = descriptor.templates.find((entry) => entry.provider === providerId);
  return template?.notificationUrl ?? template?.webhook?.notificationUrl ?? null;
}

function isOAuthManaged(descriptor: PaymentsConnectionDescriptor): boolean {
  return (
    descriptor.credentialSource === 'oauth' ||
    descriptor.oauthTokenExpiresAt !== null ||
    descriptor['oauthManaged'] === true ||
    descriptor['connectMode'] === 'oauth'
  );
}

/**
 * A retained OAuth connection whose grant was revoked: pay-api keeps the row
 * configured with tokens absent, so reconnect is required before activation.
 */
function isDeauthorizedOAuth(descriptor: PaymentsConnectionDescriptor): boolean {
  return (
    descriptor.credentialSource === 'oauth' &&
    !descriptor.active &&
    descriptor.oauthTokenExpiresAt === null &&
    descriptor.accessTokenSet !== true
  );
}

function validateClientFields(
  fields: PaymentsFieldDescriptor[],
  values: Record<string, string>,
  descriptor: PaymentsConnectionDescriptor | null,
): ValidationErrors {
  const errors: ValidationErrors = {};
  for (const field of fields) {
    const value = values[field.key] ?? '';
    const saved = descriptor !== null && secretPresence(descriptor, field.key);
    if (field.required && value.trim().length === 0 && !(isSecretField(field) && saved)) {
      errors[field.key] = 'Required';
      continue;
    }
    if (field.validationRegex !== null && value.trim().length > 0) {
      try {
        if (!new RegExp(field.validationRegex).test(value)) {
          errors[field.key] = 'Does not match the required format';
        }
      } catch {
        // Backend is authoritative; a bad registry regex should not break submit.
      }
    }
  }
  return errors;
}

function minorUnitFactor(currency: string): number {
  const normalized = currency.toUpperCase();
  if (['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'].includes(normalized)) {
    return 1;
  }
  if (['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'].includes(normalized)) {
    return 1000;
  }
  return 100;
}

function minorToMajor(value: number | null, currency: string): string {
  if (value === null) return '';
  const factor = minorUnitFactor(currency);
  const decimals = factor === 1 ? 0 : factor === 1000 ? 3 : 2;
  return (value / factor).toFixed(decimals);
}

function formatMoneyMinor(value: number | null, currency: string): string | null {
  if (value === null || currency.trim().length === 0) return null;
  const amount = Number(minorToMajor(value, currency));
  if (!Number.isFinite(amount)) return null;
  const factor = minorUnitFactor(currency);
  const decimals = factor === 1 ? 0 : factor === 1000 ? 3 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch {
    return `${amount.toFixed(decimals)} ${currency}`;
  }
}

function majorToMinor(value: string, currency: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * minorUnitFactor(currency));
}

function rowsFromDescriptor(descriptor: PaymentsConnectionDescriptor | null): CurrencyBandFormRow[] {
  if (descriptor === null || descriptor.currencies.length === 0) return [];
  return descriptor.currencies.map((band) => ({
    currency: band.currency,
    minMajor: minorToMajor(band.minChargeMinor, band.currency),
    maxMajor: minorToMajor(band.maxChargeMinor, band.currency),
  }));
}

function emptyCurrencyBandRow(currency: string): CurrencyBandFormRow {
  return { currency, minMajor: '', maxMajor: '' };
}

function seededCurrencyRows(
  provider: PaymentsProviderRegistry | null,
  descriptor: PaymentsConnectionDescriptor | null,
  currency: string,
): CurrencyBandFormRow[] {
  const descriptorRows = rowsFromDescriptor(descriptor);
  if (provider === null || provider.multiCurrency || !providerRequiresBands(provider)) {
    return descriptorRows;
  }
  if (descriptorRows.length > 0) {
    const firstRow = descriptorRows[0]!;
    return [
      {
        currency: currency || firstRow.currency,
        minMajor: firstRow.minMajor,
        maxMajor: firstRow.maxMajor,
      },
    ];
  }
  return [emptyCurrencyBandRow(currency)];
}

function validateCurrencyGuards(
  provider: PaymentsProviderRegistry,
  rows: CurrencyBandFormRow[],
  overridePinValue: string,
  descriptor: PaymentsConnectionDescriptor | null,
): ValidationErrors {
  const errors: ValidationErrors = {};
  if (providerRequiresBands(provider)) {
    if (rows.length === 0) {
      errors['currencies'] = 'Add at least one currency with min and max charge bands.';
    }
    rows.forEach((row, index) => {
      if (row.currency.trim().length === 0) errors[`currencies.${index}.currency`] = 'Required';
      const min = majorToMinor(row.minMajor, row.currency);
      const max = majorToMinor(row.maxMajor, row.currency);
      if (min === null) errors[`currencies.${index}.minChargeMinor`] = 'Required';
      if (max === null) errors[`currencies.${index}.maxChargeMinor`] = 'Required';
      if (min !== null && max !== null && min > max) {
        errors[`currencies.${index}.maxChargeMinor`] = 'Max must be greater than or equal to min';
      }
    });
  }
  const pinField = overridePinDescriptor(provider);
  if (pinField !== null && overridePinValue.trim().length > 0 && !/^\d{4,8}$/.test(overridePinValue.trim())) {
    errors[pinField.key] = 'Use 4-8 digits';
  }
  if (
    pinField !== null &&
    pinField.required &&
    descriptor?.overridePinSet !== true &&
    overridePinValue.trim().length === 0
  ) {
    errors[pinField.key] = 'Set an Override PIN before activating.';
  }
  return errors;
}

/**
 * The complete bands only. A row missing its min or max never goes on the wire: pay-api rejects it
 * as a raw 400 VALIDATION_FAILED ("currency USD requires minChargeMinor and maxChargeMinor") before
 * its activation gate runs. The editor seeds one empty row for a band-requiring provider, and the
 * OAuth Activate path skips the client band check, so that row used to reach pay-api (DNKADM-0417).
 */
function currencyRowsToWire(rows: CurrencyBandFormRow[]): PaymentsCurrencyBand[] {
  return rows
    .map((row) => ({
      currency: row.currency,
      minChargeMinor: majorToMinor(row.minMajor, row.currency),
      maxChargeMinor: majorToMinor(row.maxMajor, row.currency),
    }))
    .filter((row) => row.currency.trim().length > 0 && row.minChargeMinor !== null && row.maxChargeMinor !== null);
}

/**
 * Attach the bands to a PUT body only when at least one is complete. `currencies` is a FULL REPLACE on
 * pay-api, so an empty array would wipe the stored bands; omitting it keeps them, and when none are
 * stored pay-api's activation gate answers with the stable 409 CURRENCY_BANDS_REQUIRED (DNKADM-0417).
 */
function attachCurrencyBands(body: PaymentsConnectionInput, rows: CurrencyBandFormRow[]): void {
  const bands = currencyRowsToWire(rows);
  if (bands.length > 0) body.currencies = bands;
}

function currencyGuardsAvailable(
  provider: PaymentsProviderRegistry | null,
  descriptor: PaymentsConnectionDescriptor | null,
): boolean {
  return Boolean(
    (provider !== null && providerRequiresBands(provider)) ||
      (provider !== null && overridePinDescriptor(provider) !== null) ||
      (descriptor !== null && (descriptor.currencies.length > 0 || descriptor.overridePinSet)),
  );
}

function activationReadiness(
  provider: PaymentsProviderRegistry | null,
  descriptor: PaymentsConnectionDescriptor,
): ActivationReadiness {
  const requiresValidation =
    descriptor.validationStatus === 'unvalidated' || descriptor.validationStatus === 'invalid';
  const guardsAvailable = currencyGuardsAvailable(provider, descriptor);
  const missingCurrencyBands = guardsAvailable && descriptor.currencies.length === 0;
  const missingOverridePin = guardsAvailable && !descriptor.overridePinSet;
  // DNKADM-0323 §4c took the two guard flags out of `activationBlocked` (advisory only). DNKADM-0417 U1
  // makes them gate again, but in the setup checklist's Activate button, not here: this flag stays the
  // validation / provider-location pair that `ActivationChecklist` combines with the guard flags.
  const activationBlocked = requiresValidation || descriptor.locationSelectionRequired;
  return {
    requiresValidation,
    guardsAvailable,
    missingCurrencyBands,
    missingOverridePin,
    activationBlocked,
  };
}

function fieldErrorKey(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const fromDetails = err.details?.['key'];
  return typeof fromDetails === 'string' && fromDetails.length > 0 ? fromDetails : null;
}

/**
 * pay-api validation text names its wire fields (`minChargeMinor`, `maxChargeMinor`). Those names are
 * for developers; an owner reads the charge rules as the smallest and largest single charge
 * (DNKADM-0417). The raw text stays available under Diagnostics on the activation error.
 */
function ownerFieldWording(text: string): string {
  return text.replace(/\bminChargeMinor\b/g, 'smallest charge').replace(/\bmaxChargeMinor\b/g, 'largest charge');
}

function paymentErrorCopy(err: unknown): string {
  if (err instanceof PaymentsConnectUrlError) {
    return "Couldn't start the connection — unexpected connect URL.";
  }
  const msg = ownerFieldWording(errorMessage(err, 'Payments settings could not be updated.'));
  switch (v2ErrorCode(err)) {
    // The activation-gate 409s carry a server `message` that is safe to display; prefer it
    // and fall back to the screen's own copy when it is absent (DNKADM-0244).
    case 'VALIDATION_REQUIRED':
      return `${nonEmptyServerMessage(err) ?? 'Test the connection before activating.'}${requestSuffix(err)}`;
    case 'LOCATION_SELECTION_REQUIRED':
      return `${nonEmptyServerMessage(err) ?? 'Choose a provider location before activating.'}${requestSuffix(err)}`;
    case 'CURRENCY_BANDS_REQUIRED':
      return `Add at least one currency with min and max charge bands before activating.${requestSuffix(err)}`;
    case 'OVERRIDE_PIN_REQUIRED':
      return `Set an Override PIN before activating.${requestSuffix(err)}`;
    case 'OAUTH_MANAGED':
      return `This connection is managed by OAuth. Use Reconnect or edit payment settings only.${requestSuffix(err)}`;
    case 'PROVIDER_MIXED_TENANT':
      return msg;
    case 'VALIDATION_FAILED':
      return msg;
    case 'CONNECT_NOT_CONFIGURED':
      return `OAuth connect not configured for this environment; contact support${requestSuffix(err)}`;
    case 'CONNECT_UNAVAILABLE':
      return `Production payments aren’t available yet. Contact support to enable production.${requestSuffix(err)}`;
    default:
      return msg;
  }
}

function nonEmptyServerMessage(err: unknown): string | null {
  const msg = serverMessage(err);
  return msg !== null && msg.trim().length > 0 ? msg : null;
}

/** What an activation failure asks the owner to do next; each maps to one button (DNKADM-0417). */
type ActivationFix = 'charge-rules' | 'override-pin' | 'test-connection' | 'provider-location';

interface ActivationErrorView {
  message: string;
  fix: ActivationFix | null;
  requestId: string | null;
  /** The raw code and server text, shown only under Diagnostics. */
  diagnostics: string | null;
}

const ACTIVATION_FIX_LABEL: Record<ActivationFix, string> = {
  'charge-rules': 'Set charge rules',
  'override-pin': 'Set PIN',
  'test-connection': 'Run test',
  'provider-location': 'Choose location',
};

// pay-api's band and PIN validation arrives as a generic 400 VALIDATION_FAILED with no field detail,
// so its text is the only thing to match. The 409 gate codes are the stable contract; this is the
// defensive net for the validation shape (DNKADM-0417 U2). Only "requires minChargeMinor and
// maxChargeMinor" means the rules are missing. pay-api's other band errors (min <= 0, max < min, over the
// platform cap) and its PIN format error (`ConnectionCurrencyService.validate`,
// `ConnectionService.applyOverridePin`) keep their specific reason, in owner words; a missing PIN only
// ever arrives as the 409 OVERRIDE_PIN_REQUIRED.
const MISSING_BANDS_TEXT = /requires minChargeMinor and maxChargeMinor/i;
const OTHER_BAND_TEXT = /minChargeMinor|maxChargeMinor/;
const PIN_VALIDATION_TEXT = /override pin/i;

function withoutTrailingPeriod(text: string): string {
  return text.trim().replace(/\.$/, '');
}

function activationErrorView(err: unknown): ActivationErrorView {
  const code = v2ErrorCode(err);
  const raw = nonEmptyServerMessage(err);
  const requestId = err instanceof ApiError && err.requestId ? err.requestId : null;
  const rawParts = [code, raw].filter((part): part is string => part !== null && part !== undefined);
  const diagnostics = rawParts.length > 0 ? rawParts.join(': ') : null;
  const view = (message: string, fix: ActivationFix | null): ActivationErrorView => ({
    message,
    fix,
    requestId,
    diagnostics,
  });
  switch (code) {
    case 'CURRENCY_BANDS_REQUIRED':
      return view('Set charge rules before turning payments on.', 'charge-rules');
    case 'OVERRIDE_PIN_REQUIRED':
      return view('Set an override PIN before turning payments on.', 'override-pin');
    case 'VALIDATION_REQUIRED':
      return view('Test the connection before turning payments on.', 'test-connection');
    case 'LOCATION_SELECTION_REQUIRED':
      return view('Choose which provider location takes payments before turning payments on.', 'provider-location');
    default:
      break;
  }
  if (raw !== null && MISSING_BANDS_TEXT.test(raw)) {
    return view('Set charge rules before turning payments on.', 'charge-rules');
  }
  if (raw !== null && OTHER_BAND_TEXT.test(raw)) {
    return view(`Check your charge rules: ${withoutTrailingPeriod(ownerFieldWording(raw))}.`, 'charge-rules');
  }
  if (raw !== null && PIN_VALIDATION_TEXT.test(raw)) {
    return view(`Check your override PIN: ${withoutTrailingPeriod(raw)}.`, 'override-pin');
  }
  return view(raw !== null ? ownerFieldWording(raw) : 'Payments could not be turned on. Try again.', null);
}

function connectOAuthErrorView(err: unknown): ConnectOAuthErrorView {
  const msg = nonEmptyServerMessage(err);
  switch (v2ErrorCode(err)) {
    case 'VALIDATION_FAILED':
      return {
        message: `${msg ?? "This site's payment return URL is not registered. Contact support to register it."}${requestSuffix(err)}`,
        retryable: false,
      };
    case 'CONNECT_NOT_CONFIGURED':
      return {
        message: `${msg ?? 'OAuth connect not configured for this environment; contact support'}${requestSuffix(err)}`,
        retryable: false,
      };
    case 'CONNECT_UNAVAILABLE':
      return {
        message: `${msg ?? 'Production payments aren’t available yet. Contact support to enable production.'}${requestSuffix(err)}`,
        retryable: err instanceof ApiError && err.retryable === true,
      };
    case 'PROVIDER_ERROR':
      return {
        message: `${msg ?? 'Payment provider connection is temporarily unavailable. Try again.'}${requestSuffix(err)}`,
        retryable: err instanceof ApiError && err.retryable === true,
      };
    default:
      return { message: paymentErrorCopy(err), retryable: false };
  }
}

function isHandledConnectSessionError(err: unknown): boolean {
  return ['VALIDATION_FAILED', 'CONNECT_NOT_CONFIGURED', 'CONNECT_UNAVAILABLE', 'PROVIDER_ERROR'].includes(
    v2ErrorCode(err) ?? '',
  );
}

function saveConnectContext(context: ConnectContext): void {
  const body: Record<string, string> = {
    provider: context.provider,
    environment: context.environment,
  };
  if (context.locationRef !== undefined) body['locationRef'] = context.locationRef;
  sessionStorage.setItem(CONNECT_CONTEXT_KEY, JSON.stringify(body));
}

function readConnectContext(): ConnectContext | null {
  try {
    const raw = sessionStorage.getItem(CONNECT_CONTEXT_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<ConnectContext>;
    if (typeof parsed.provider !== 'string' || typeof parsed.environment !== 'string') return null;
    const context: ConnectContext = {
      provider: parsed.provider,
      environment: parsed.environment,
    };
    if (typeof parsed.locationRef === 'string' && parsed.locationRef.length > 0) {
      context.locationRef = parsed.locationRef;
    }
    return context;
  } catch {
    return null;
  }
}

export function PaymentsConnectionScreen() {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  if (next.has('connect') && !next.has('locationRef') && !next.has('configure')) {
    next.set('configure', 'default');
  }
  const query = next.toString();
  return <Navigate to={`/settings/payments/overview${query.length > 0 ? `?${query}` : ''}`} replace />;
}

interface PaymentsConnectionEditorProps {
  /**
   * Called after anything that can change the connection's stored state (activate, deactivate,
   * charge rules or PIN saved, test, provider location, reconnect, disconnect), so a host that shows
   * the same connection elsewhere — the Overview row, Health chip and banner — can refetch in place
   * without a page reload (DNKADM-0417 U3). It receives the location the change was made for, captured
   * when the action ran (undefined = the tenant-default connection).
   */
  onConnectionChanged?: (locationRef: string | undefined) => void;
}

export function PaymentsConnectionEditor({ onConnectionChanged }: PaymentsConnectionEditorProps = {}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const showToast = useToast();
  const onConnectionChangedRef = useRef(onConnectionChanged);
  useEffect(() => {
    onConnectionChangedRef.current = onConnectionChanged;
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const handledConnectRef = useRef(false);
  const descriptorRunRef = useRef(0);
  const observabilityRunRef = useRef(0);

  const [registryKey, setRegistryKey] = useState(0);
  const [locationsKey, setLocationsKey] = useState(0);
  const [registry, setRegistry] = useState<LoadState<PaymentsRegistry>>(loading());
  const [locations, setLocations] = useState<LoadState<V2Location[]>>(loading());
  // Seed the initial location selection from a `?locationRef=` deep-link (the dashboard's
  // entry point) — read once at mount, never synced afterward. No roster validation: a
  // stale id degrades to the screen's existing unconfigured/descriptor-error handling. The
  // OAuth-return restore effect still overwrites this when `connect` is present, and
  // dashboard links never carry `connect`, so there is no conflict (DNKADM-0132).
  const [locationValue, setLocationValue] = useState(
    () => searchParams.get('locationRef') ?? TENANT_DEFAULT,
  );
  const locationRef = selectedLocationRef(locationValue);
  const [descriptor, setDescriptor] = useState<LoadState<PaymentsConnectionDescriptor>>(loading());
  const [status, setStatus] = useState<PaymentsStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<PaymentsWebhookStatus>({ available: false });
  const [webhookStatusLoading, setWebhookStatusLoading] = useState(false);
  const [attention, setAttention] = useState<PaymentsAttention>({ available: false });
  const [sendingWebhookTest, setSendingWebhookTest] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState<PaymentsWebhookTestResult | null>(null);
  const [webhookTestError, setWebhookTestError] = useState<string | null>(null);
  const [webhookTestRateLimited, setWebhookTestRateLimited] = useState(false);

  const [pickedProvider, setPickedProvider] = useState<string | null>(null);
  const [pickedMode, setPickedMode] = useState<'oauth' | 'manual' | null>(null);
  const [environment, setEnvironment] = useState('');
  const [currency, setCurrency] = useState('');
  const [currencyRows, setCurrencyRows] = useState<CurrencyBandFormRow[]>([]);
  const [overridePin, setOverridePin] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [webhookValues, setWebhookValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<ConnectOAuthErrorView | null>(null);
  const [saving, setSaving] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectGuidance, setDisconnectGuidance] = useState<string | null>(null);
  const [pspLocations, setPspLocations] = useState<LoadState<PaymentsPspLocation[]>>(loading());
  const [selectedPspLocation, setSelectedPspLocation] = useState('');
  const [savingPsp, setSavingPsp] = useState(false);
  const [changeProviderOpen, setChangeProviderOpen] = useState(false);
  const [chargeRulesOpen, setChargeRulesOpen] = useState(false);
  const [activationError, setActivationError] = useState<ActivationErrorView | null>(null);
  const [manualSwitchOpen, setManualSwitchOpen] = useState(false);
  const [manualConversionMode, setManualConversionMode] = useState(false);
  const detailStartRef = useRef<HTMLDivElement | null>(null);
  const restoredConnectContextRef = useRef<ConnectContext | null>(null);

  useEffect(() => {
    let alive = true;
    setRegistry(loading());
    void getPaymentsRegistry()
      .then((data) => {
        if (alive) setRegistry(ready(data));
      })
      .catch((err) => {
        if (alive) setRegistry(failed(err));
      });
    return () => {
      alive = false;
    };
  }, [registryKey]);

  useEffect(() => {
    let alive = true;
    setLocations(loading());
    void listLocations()
      .then((data) => {
        if (alive) setLocations(ready(data));
      })
      .catch((err) => {
        if (alive) setLocations(failed(err));
      });
    return () => {
      alive = false;
    };
  }, [locationsKey]);

  const loadDescriptor = useCallback(async () => {
    const run = descriptorRunRef.current + 1;
    descriptorRunRef.current = run;
    setDescriptor(loading());
    setStatus(null);
    try {
      const data = await getPaymentsConnection(locationRef);
      if (descriptorRunRef.current !== run) return;
      setDescriptor(ready(data));
    } catch (err) {
      if (descriptorRunRef.current !== run) return;
      setDescriptor(failed(err));
    }
  }, [locationRef]);

  useEffect(() => {
    void loadDescriptor();
  }, [loadDescriptor]);

  /**
   * Re-read this connection, then let the host refetch its own view of it (DNKADM-0417 U3). Any
   * activation error is cleared here: it described the state before this change (e.g. "Test the
   * connection" after the test just ran), and the re-read checklist now says what is left. The
   * activation-failure paths never call this, so a fresh failure stays on screen.
   */
  const reloadAfterChange = useCallback(async () => {
    const changedLocationRef = locationRef;
    setActivationError(null);
    await loadDescriptor();
    onConnectionChangedRef.current?.(changedLocationRef);
  }, [loadDescriptor, locationRef]);

  const loadObservabilityFor = useCallback(
    async (data: PaymentsConnectionDescriptor) => {
      const run = observabilityRunRef.current + 1;
      observabilityRunRef.current = run;
      const shouldLoadWebhook = data.configured && data.active;
      setWebhookStatusLoading(shouldLoadWebhook);
      const [nextAttention, nextWebhookStatus] = await Promise.all([
        getPaymentsAttention().catch((): PaymentsAttention => ({ available: false })),
        shouldLoadWebhook
          ? getWebhookStatus(locationRef).catch((): PaymentsWebhookStatus => ({ available: false }))
          : Promise.resolve<PaymentsWebhookStatus>({ available: false }),
      ]);
      if (observabilityRunRef.current !== run) return;
      setAttention(nextAttention);
      setWebhookStatus(nextWebhookStatus);
      setWebhookStatusLoading(false);
    },
    [locationRef],
  );

  useEffect(() => {
    if (descriptor.status !== 'ready') {
      observabilityRunRef.current += 1;
      setAttention({ available: false });
      setWebhookStatus({ available: false });
      setWebhookStatusLoading(false);
      setWebhookTestResult(null);
      setWebhookTestError(null);
      setWebhookTestRateLimited(false);
      return;
    }
    setWebhookTestResult(null);
    setWebhookTestError(null);
    setWebhookTestRateLimited(false);
    void loadObservabilityFor(descriptor.data);
  }, [descriptor, loadObservabilityFor]);

  useEffect(() => {
    if (descriptor.status !== 'ready') return;
    const data = descriptor.data;
    if (!data.configured || !data.active) return;
    let alive = true;
    setStatusLoading(true);
    void getPaymentsStatus(locationRef)
      .then((next) => {
        if (alive) setStatus(next);
      })
      .catch(() => {
        if (alive) setStatus(null);
      })
      .finally(() => {
        if (alive) setStatusLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [descriptor, locationRef]);

  const registryData = registry.status === 'ready' ? registry.data : null;
  const descriptorData = descriptor.status === 'ready' ? descriptor.data : null;

  /**
   * pay-api's PUT is NOT keep-on-omit for `active`: an upsert deactivates unless the body
   * carries `active: true`. So every save echoes the connection's CURRENT stored value.
   * Never hardcode `true` — that would activate a connection the admin never asked to
   * activate (and 409 on an ineligible one). An unconfigured/new connection echoes `false`,
   * which is what pay-api already does for an omitted value (DNKADM-0244).
   *
   * The read-before-write is deliberately non-atomic: a concurrent change elsewhere can make
   * the echoed value stale between the descriptor read and this save. Accepted for an admin
   * screen — no locking or optimistic concurrency here.
   */
  const storedActive = descriptorData?.active === true;

  const selectedProvider = useMemo(() => {
    if (registryData === null || pickedProvider === null) return null;
    return registryData.providers.find((provider) => provider.provider === pickedProvider) ?? null;
  }, [registryData, pickedProvider]);

  // Seed the form from a saved, configured descriptor. Extracted from the
  // descriptor-load effect so the Cancel-manual-switch handler can revert to the
  // exact same OAuth baseline as a fresh descriptor load (DNKADM-0131).
  const seedFormFromConfiguredDescriptor = useCallback(
    (data: PaymentsConnectionDescriptor) => {
      const provider =
        registryData?.providers.find((entry) => entry.provider === data.provider) ?? null;
      setPickedProvider(data.provider);
      setPickedMode(isOAuthManaged(data) ? 'oauth' : 'manual');
      setEnvironment(
        resolvedEnvironment(
          data.environment,
          provider ? connectionField(provider, 'environment') : null,
          data.environmentPermitted,
        ),
      );
      const descriptorRows = rowsFromDescriptor(data);
      const fallbackCurrency = firstOption(provider ? connectionField(provider, 'currency') : null);
      const descriptorCurrency =
        provider?.multiCurrency === true && descriptorRows.length > 0
          ? descriptorRows.find((row) => row.currency === data.currency)?.currency ??
            descriptorRows[0]!.currency
          : data.currency ?? fallbackCurrency;
      setCurrency(descriptorCurrency);
      setCurrencyRows(seededCurrencyRows(provider, data, descriptorCurrency));
      setOverridePin('');
      setFieldValues({ ...data.fields });
      setWebhookValues({});
      setFieldErrors({});
      setFormError(null);
      setOauthError(null);
    },
    [registryData],
  );

  useEffect(() => {
    if (descriptorData === null || registryData === null) return;
    if (descriptorData.configured && descriptorData.provider !== null) {
      restoredConnectContextRef.current = null;
      seedFormFromConfiguredDescriptor(descriptorData);
    } else {
      const restored = restoredConnectContextRef.current;
      restoredConnectContextRef.current = null;
      if (restored !== null && selectValue(restored.locationRef) === locationValue) {
        setPickedProvider(restored.provider);
        setPickedMode('oauth');
        setEnvironment(restored.environment);
        setCurrency('');
        setCurrencyRows([]);
        setOverridePin('');
        setFieldValues({});
        setWebhookValues({});
        setFieldErrors({});
        setFormError(null);
        setOauthError(null);
        setDisconnectGuidance(null);
        setManualConversionMode(false);
        return;
      }
      setPickedProvider(null);
      setPickedMode(null);
      setEnvironment('');
      setCurrency('');
      setCurrencyRows([]);
      setOverridePin('');
      setFieldValues({});
      setWebhookValues({});
      setFieldErrors({});
      setFormError(null);
      setOauthError(null);
      setDisconnectGuidance(null);
      setManualConversionMode(false);
    }
  }, [descriptorData, registryData, locationValue, seedFormFromConfiguredDescriptor]);

  useEffect(() => {
    setManualConversionMode(false);
  }, [locationRef]);

  useEffect(() => {
    const connect = searchParams.get('connect');
    if (connect === null || handledConnectRef.current) return;
    handledConnectRef.current = true;
    const context = readConnectContext();
    if (context !== null) {
      restoredConnectContextRef.current = context;
      setLocationValue(selectValue(context.locationRef));
      setPickedProvider(context.provider);
      setPickedMode('oauth');
      setEnvironment(context.environment);
    }
    if (connect === 'success') {
      showToast('Payment provider connected. Review the connection status.', 'success');
    } else {
      const reason = searchParams.get('reason');
      const message =
        reason === 'denied'
          ? 'Payment provider access was denied.'
          : reason === 'exchange_failed'
            ? 'Payment provider authorization could not be completed.'
            : reason === 'no_locations'
              ? 'No provider locations were returned.'
              : reason === 'provider_conflict'
                ? 'The provider is already connected elsewhere.'
                : 'Payment provider connection did not complete.';
      showToast(message, 'error');
    }
    void loadDescriptor();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('connect');
        next.delete('reason');
        if (context?.locationRef !== undefined) {
          next.set('locationRef', context.locationRef);
          next.delete('configure');
        } else if (!next.has('locationRef')) {
          next.set('configure', 'default');
        }
        return next;
      },
      { replace: true },
    );
  }, [loadDescriptor, searchParams, setSearchParams, showToast]);

  useEffect(() => {
    if (descriptorData?.locationSelectionRequired !== true) return;
    let alive = true;
    setPspLocations(loading());
    void getPspLocations(locationRef)
      .then((data) => {
        if (!alive) return;
        setPspLocations(ready(data));
        setSelectedPspLocation(data[0]?.id ?? '');
      })
      .catch((err) => {
        if (alive) setPspLocations(failed(err));
      });
    return () => {
      alive = false;
    };
  }, [descriptorData?.locationSelectionRequired, locationRef]);

  function chooseProvider(provider: PaymentsProviderRegistry) {
    applyProviderChoice(provider);
  }

  function applyProviderChoice(provider: PaymentsProviderRegistry) {
    if (descriptorData?.configured === true && descriptorData.provider === provider.provider) {
      setPickedProvider(provider.provider);
      setPickedMode(isOAuthManaged(descriptorData) ? 'oauth' : 'manual');
      setFormError(null);
      setOauthError(null);
      return;
    }
    setPickedProvider(provider.provider);
    setPickedMode(provider.connectModes.includes('oauth') ? 'oauth' : 'manual');
    setEnvironment(resolvedEnvironment(null, connectionField(provider, 'environment')));
    const nextCurrency = firstOption(connectionField(provider, 'currency'));
    setCurrency(nextCurrency);
    setCurrencyRows(seededCurrencyRows(provider, null, nextCurrency));
    setOverridePin('');
    setFieldValues({});
    setWebhookValues({});
    setFieldErrors({});
    setFormError(null);
    setOauthError(null);
    setDisconnectGuidance(null);
    setManualConversionMode(false);
  }

  const replacingConfiguredProvider =
    descriptorData?.configured === true &&
    descriptorData.provider !== null &&
    pickedProvider !== null &&
    pickedProvider !== descriptorData.provider;

  function buildConnectionBody(
    provider: PaymentsProviderRegistry,
    options: { active?: boolean; forceProvider?: string } = {},
  ): PaymentsConnectionInput {
    const fields: Record<string, string> = {};
    for (const field of provider.descriptors) {
      const value = fieldValues[field.key] ?? '';
      if (isSecretField(field)) {
        if (value.trim().length > 0) fields[field.key] = value;
      } else {
        fields[field.key] = value;
      }
    }
    const webhook: Record<string, string> = {};
    for (const field of provider.webhookFields) {
      const value = webhookValues[field.key] ?? '';
      if (isSecretField(field)) {
        if (value.trim().length > 0) webhook[field.key] = value;
      } else if (value.trim().length > 0) {
        webhook[field.key] = value;
      }
    }
    const pinField = overridePinDescriptor(provider);
    const body: PaymentsConnectionInput = {
      provider: options.forceProvider ?? provider.provider,
      environment,
      currency,
      fields,
      ...(Object.keys(webhook).length > 0 ? { webhook } : {}),
      active: options.active ?? storedActive,
    };
    if (providerRequiresBands(provider)) {
      attachCurrencyBands(body, currencyRows);
    }
    if (pinField !== null && overridePin.trim().length > 0) {
      body.overridePin = overridePin.trim();
    }
    return body;
  }

  async function requestOAuthConnectUrl(provider: PaymentsProviderRegistry): Promise<string> {
    const permission =
      descriptorData?.configured === true && descriptorData.provider === provider.provider
        ? descriptorData.environmentPermitted
        : null;
    const env = resolvedEnvironment(
      environment,
      connectionField(provider, 'environment'),
      permission,
    );
    const context: ConnectContext = { provider: provider.provider, environment: env };
    if (locationRef !== undefined) context.locationRef = locationRef;
    saveConnectContext(context);
    const returnUrl = `${window.location.origin}/settings/payments`;
    const session = await startConnectSession({ ...context, returnUrl });
    if (session.connectUrl.length === 0) throw new Error('Missing connect URL');
    return validatePaymentsConnectUrl(session.connectUrl);
  }

  function surfaceConnectError(err: unknown) {
    const errorView = connectOAuthErrorView(err);
    setOauthError(errorView);
    if (!(err instanceof PaymentsConnectUrlError) && !isHandledConnectSessionError(err)) {
      showToast(errorView.message, 'error');
    }
  }

  async function startOAuth() {
    if (selectedProvider === null) return;
    const tab = window.open('', '_blank');
    if (tab === null) {
      setOauthError({
        message: 'Your browser blocked the new tab. Allow pop-ups and try again.',
        retryable: true,
      });
      return;
    }
    tab.opener = null;
    setOauthError(null);
    setSaving(true);
    setRedirecting(true);
    let shouldReloadDescriptor = false;
    try {
      const connectUrl = await requestOAuthConnectUrl(selectedProvider);
      tab.location.replace(connectUrl);
      shouldReloadDescriptor = true;
    } catch (err) {
      tab.close();
      surfaceConnectError(err);
    } finally {
      setRedirecting(false);
      setSaving(false);
      if (shouldReloadDescriptor) await reloadAfterChange();
    }
  }

  async function copyOAuthLink() {
    if (selectedProvider === null) return;
    const clipboard = navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== 'function') {
      setOauthError({
        message: 'Clipboard access is unavailable in this browser. Open the connection in a new tab instead.',
        retryable: false,
      });
      return;
    }
    setOauthError(null);
    setSaving(true);
    try {
      const connectUrl = await requestOAuthConnectUrl(selectedProvider);
      try {
        await clipboard.writeText(connectUrl);
      } catch {
        setOauthError({
          message: "Couldn't copy the connection link. Check clipboard permissions and try again.",
          retryable: false,
        });
        return;
      }
      showToast('Connection link copied to clipboard.', 'success');
    } catch (err) {
      surfaceConnectError(err);
    } finally {
      setSaving(false);
    }
  }

  async function saveManual(options: { active?: boolean; forceProvider?: string } = {}) {
    if (selectedProvider === null) return false;
    const values = { ...fieldValues };
    const convertingOAuth = descriptorData !== null && isOAuthManaged(descriptorData) && !replacingConfiguredProvider;
    const credentialDescriptor = replacingConfiguredProvider || convertingOAuth ? null : descriptorData;
    const errors = validateClientFields(selectedProvider.descriptors, values, credentialDescriptor);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const body = buildConnectionBody(selectedProvider, options);
      if (descriptorData !== null && isOAuthManaged(descriptorData) && !replacingConfiguredProvider) {
        await convertPaymentsConnectionToManual(locationRef, body);
      } else {
        await putPaymentsConnection(locationRef, body);
      }
      setManualConversionMode(false);
      showToast('Payment connection saved', 'success');
      // Re-read on SUCCESS only. The refresh fires the hydration effect, which re-seeds the
      // form from the server AND clears fieldErrors/formError — in a `finally` that erased
      // both the admin's edits and the error they need to see on a failed save (DNKADM-0244).
      await reloadAfterChange();
      return true;
    } catch (err) {
      if (v2ErrorCode(err) === 'VALIDATION_FAILED') {
        const key = fieldErrorKey(err);
        const allKnown = [
          ...selectedProvider.descriptors.map((field) => field.key),
          ...selectedProvider.webhookFields.map((field) => field.key),
          ...selectedProvider.connectionFields.map((field) => field.key),
        ];
        if (key !== null && allKnown.includes(key)) {
          setFieldErrors({ [key]: serverMessage(err) ?? 'Check this value.' });
        } else {
          setRegistryKey((value) => value + 1);
        }
      } else if (v2ErrorCode(err) === 'CURRENCY_BANDS_REQUIRED') {
        setFieldErrors({ currencies: 'Add at least one currency with min and max charge bands.' });
      } else if (v2ErrorCode(err) === 'OVERRIDE_PIN_REQUIRED') {
        const pinKey = overridePinDescriptor(selectedProvider)?.key ?? OVERRIDE_PIN_KEY;
        setFieldErrors({ [pinKey]: 'Set an Override PIN before activating.' });
      }
      setFormError(paymentErrorCopy(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function saveOAuthSettings(options: { active?: boolean } = {}) {
    if (selectedProvider === null) return false;
    if (options.active !== true) {
      const errors = validateCurrencyGuards(selectedProvider, currencyRows, overridePin, descriptorData);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return false;
      }
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    setActivationError(null);
    try {
      const pinField = overridePinDescriptor(selectedProvider);
      const body: PaymentsConnectionInput = {
        provider: selectedProvider.provider,
        environment,
        currency,
        active: options.active ?? storedActive,
      };
      if (providerRequiresBands(selectedProvider)) {
        attachCurrencyBands(body, currencyRows);
      }
      if (pinField !== null && overridePin.trim().length > 0) {
        body.overridePin = overridePin.trim();
      }
      await putPaymentsConnection(locationRef, body);
      showToast(options.active === true ? 'Payment connection activated' : 'Payment connection saved', 'success');
      // Re-read on SUCCESS only — see the note in saveManual (DNKADM-0244).
      await reloadAfterChange();
      return true;
    } catch (err) {
      // ONLY an OAuth-managed rejection means "this form cannot edit credentials". The
      // activation-gate 409s below are correctable by the admin — never lock the form on them.
      if (v2ErrorCode(err) === 'CURRENCY_BANDS_REQUIRED') {
        setFieldErrors({ currencies: 'Add at least one currency with min and max charge bands.' });
      } else if (v2ErrorCode(err) === 'OVERRIDE_PIN_REQUIRED') {
        const pinKey = overridePinDescriptor(selectedProvider)?.key ?? OVERRIDE_PIN_KEY;
        setFieldErrors({ [pinKey]: 'Set an Override PIN before activating.' });
      }
      if (options.active === true) {
        surfaceActivationError(err);
      } else {
        setFormError(paymentErrorCopy(err));
        showToast(paymentErrorCopy(err), 'error');
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** Owner copy + a button to the fix; the raw code and text only under Diagnostics (DNKADM-0417 U2). */
  function surfaceActivationError(err: unknown) {
    const view = activationErrorView(err);
    setActivationError(view);
    showToast(view.message, 'error');
  }

  async function validateConnection() {
    setValidating(true);
    setFormError(null);
    try {
      const result = await validatePaymentsConnection(locationRef);
      showToast(
        result.validationStatus === 'valid' ? 'Connection verified' : 'Connection test failed',
        result.validationStatus === 'valid' ? 'success' : 'error',
      );
    } catch (err) {
      setFormError(paymentErrorCopy(err));
      showToast(paymentErrorCopy(err), 'error');
    } finally {
      setValidating(false);
      await reloadAfterChange();
    }
  }

  async function setActive(active: boolean) {
    if (selectedProvider === null) return;
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    setActivationError(null);
    try {
      await putPaymentsConnection(locationRef, buildConnectionBody(selectedProvider, { active }));
      showToast(active ? 'Payment connection activated' : 'Payment connection updated', 'success');
      await reloadAfterChange();
    } catch (err) {
      if (v2ErrorCode(err) === 'CURRENCY_BANDS_REQUIRED') {
        setFieldErrors({ currencies: 'Add at least one currency with min and max charge bands.' });
      } else if (v2ErrorCode(err) === 'OVERRIDE_PIN_REQUIRED') {
        const pinKey = overridePinDescriptor(selectedProvider)?.key ?? OVERRIDE_PIN_KEY;
        setFieldErrors({ [pinKey]: 'Set an Override PIN before activating.' });
      }
      if (active) {
        surfaceActivationError(err);
      } else {
        setFormError(paymentErrorCopy(err));
        showToast(paymentErrorCopy(err), 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  async function deactivate() {
    setDeactivating(true);
    setFormError(null);
    try {
      await deletePaymentsConnection(locationRef);
      showToast('Payment connection deactivated', 'success');
    } catch (err) {
      setFormError(paymentErrorCopy(err));
      showToast(paymentErrorCopy(err), 'error');
    } finally {
      setDeactivating(false);
      await reloadAfterChange();
    }
  }

  async function disconnectOAuth() {
    setDisconnecting(true);
    setFormError(null);
    setDisconnectGuidance(null);
    try {
      const result = await disconnectPaymentsOAuth(locationRef);
      if (!result.pspRevoked) {
        setDisconnectGuidance('Disconnected here; also remove access in your Square dashboard.');
      }
      showToast('OAuth connection disconnected', 'success');
    } catch (err) {
      setFormError(paymentErrorCopy(err));
      showToast(paymentErrorCopy(err), 'error');
    } finally {
      setDisconnectOpen(false);
      setDisconnecting(false);
      await reloadAfterChange();
    }
  }

  async function savePspLocation() {
    if (selectedPspLocation.length === 0) return;
    setSavingPsp(true);
    setFormError(null);
    try {
      await setPspLocation(locationRef, selectedPspLocation);
      showToast('Provider location selected', 'success');
    } catch (err) {
      setFormError(paymentErrorCopy(err));
      showToast(paymentErrorCopy(err), 'error');
    } finally {
      setSavingPsp(false);
      await reloadAfterChange();
    }
  }

  async function sendTestEvent() {
    setSendingWebhookTest(true);
    setWebhookTestError(null);
    setWebhookTestResult(null);
    try {
      const result = await sendWebhookTest(locationRef);
      if (!result.available) {
        setWebhookStatus({ available: false });
        return;
      }
      setWebhookTestResult(result);
      if (descriptorData !== null) {
        await loadObservabilityFor(descriptorData);
      }
    } catch (err) {
      const message = errorMessage(err, 'Couldn’t send a webhook test event.');
      setWebhookTestError(message);
      if (err instanceof ApiError && (err.status === 429 || err.code === 'RATE_LIMITED')) {
        setWebhookTestRateLimited(true);
      }
    } finally {
      setSendingWebhookTest(false);
    }
  }

  const busy = saving || validating || deactivating || disconnecting || savingPsp;

  function focusDetailControls() {
    detailStartRef.current
      ?.querySelector<HTMLElement>('input, button, textarea, [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }

  function focusProviderLocationPicker() {
    document
      .getElementById(PROVIDER_LOCATION_ANCHOR_ID)
      ?.querySelector<HTMLElement>('input, [role="combobox"], button')
      ?.focus();
  }

  function enterCredentials() {
    if (pickedMode === 'manual') {
      focusDetailControls();
      return;
    }
    if (descriptorData !== null && descriptorData.configured && isOAuthManaged(descriptorData)) {
      setManualSwitchOpen(true);
      return;
    }
    setPickedMode('manual');
    window.requestAnimationFrame(focusDetailControls);
  }

  if (registry.status === 'loading' || locations.status === 'loading' || descriptor.status === 'loading') {
    return <LoadingPayments />;
  }

  if (registry.status === 'error') {
    return (
      <ScreenError
        error={registry.error}
        what="the payments registry"
        onRetry={() => setRegistryKey((v) => v + 1)}
        retryMessage={errorMessage(registry.error, 'Couldn’t load the payments registry.')}
      />
    );
  }

  if (locations.status === 'error') {
    return (
      <ScreenError
        error={locations.error}
        what="the locations"
        onRetry={() => setLocationsKey((v) => v + 1)}
        retryMessage={errorMessage(
          locations.error,
          'Couldn’t load the locations, so payments can’t be configured safely.',
        )}
      />
    );
  }

  if (descriptor.status === 'error') {
    return (
      <ScreenError
        error={descriptor.error}
        what="the payment connection"
        onRetry={() => void loadDescriptor()}
        retryMessage={errorMessage(descriptor.error, 'Couldn’t load the payment connection.')}
      />
    );
  }

  // A configured descriptor is seeded into the form by an effect that runs after this render. Until it
  // has, every action reads a null provider and silently does nothing, so hold the skeleton for that
  // one frame instead of painting controls that cannot work yet (DNKADM-0417: the removed header
  // Tooltip had been forcing that effect to flush synchronously, which is what used to hide this).
  if (descriptorData === null || (descriptorData.configured && descriptorData.provider !== null && pickedProvider === null)) {
    return <LoadingPayments />;
  }

  const exactRowCopy =
    locationRef !== undefined && descriptorData?.configured === false
      ? 'Using tenant default — configure to override'
      : null;
  const deauthorizedOAuth = descriptorData.configured && isDeauthorizedOAuth(descriptorData);
  const liveGrant = descriptorData.configured && !deauthorizedOAuth;
  const disconnectedEditable = !descriptorData.configured || deauthorizedOAuth;
  const providerLocked = liveGrant;
  const configuredProviderLabel = providerLabel(selectedProvider, descriptorData.provider);
  const chosenProviderLabel = descriptorData.configured
    ? configuredProviderLabel
    : selectedProvider?.label ?? null;
  const configuredProviderId = descriptorData.configured ? descriptorData.provider : null;
  const currentChoiceProviderId = pickedProvider ?? configuredProviderId;
  const activate = () =>
    isOAuthManaged(descriptorData) ? void saveOAuthSettings({ active: true }) : void setActive(true);
  // DNKADM-0417 U1: a configured connection that is connected but not yet on gets the ordered setup
  // checklist in place of the raw "Payments need attention" codes for it.
  const setupPending = liveGrant && !descriptorData.active;
  const setupReadiness = setupPending ? activationReadiness(selectedProvider, descriptorData) : null;
  const visibleAttention = setupPending ? withoutSetupAttention(attention, locationRef) : attention;
  const openChargeRules = () => setChargeRulesOpen(true);
  const runActivationFix = (fix: ActivationFix) => {
    if (fix === 'charge-rules' || fix === 'override-pin') openChargeRules();
    else if (fix === 'test-connection') void validateConnection();
    else focusProviderLocationPicker();
  };
  const lockedProviderAction: ProviderSummaryAction = isOAuthManaged(descriptorData)
    ? {
        label: `Disconnect ${configuredProviderLabel}`,
        color: 'error',
        variant: 'contained',
        disabled: busy,
        onClick: () => {
          setChangeProviderOpen(false);
          setDisconnectOpen(true);
        },
      }
    : {
        label: 'Deactivate',
        color: 'error',
        variant: 'contained',
        disabled: busy,
        onClick: () => {
          setChangeProviderOpen(false);
          void deactivate();
        },
      };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h1">
          Payments
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 720 }}>
          Configure the payment connection for tenant default settings or for one location.
        </Typography>
      </Box>

      <AttentionStrip attention={visibleAttention} />

      <Stack sx={{ gap: 1, mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          {locationRef === undefined
            ? 'Editing: Tenant default connection'
            : `Editing: ${defaultLocationName(locations.data, locationRef)}`}
        </Typography>
        {exactRowCopy !== null ? (
          <Alert severity="info" sx={{ py: 0.25, alignSelf: 'flex-start' }}>
            {exactRowCopy}
          </Alert>
        ) : null}
      </Stack>

      <Box
        data-testid="payments-connection-card"
        sx={{
          maxWidth: 760,
          p: 2,
          mb: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: t.surface,
        }}
      >
        <ProviderSummary
          chosenProviderLabel={chosenProviderLabel}
          provider={selectedProvider}
          descriptor={descriptorData}
          environment={environment}
          setEnvironment={setEnvironment}
          currency={currency}
          disconnectGuidance={disconnectGuidance}
          liveGrant={liveGrant}
          disconnectedEditable={disconnectedEditable}
          pickedMode={pickedMode}
          busy={busy}
          validating={validating}
          deactivating={deactivating}
          disconnecting={disconnecting}
          onOpenChangeProvider={() => setChangeProviderOpen(true)}
          onConnect={() => void startOAuth()}
          onCopyConnectLink={() => void copyOAuthLink()}
          onEnterCredentials={enterCredentials}
          onManualSwitch={() => setManualSwitchOpen(true)}
          onValidate={() => void validateConnection()}
          onDeactivate={() => void deactivate()}
          onReconnect={() => void startOAuth()}
          onDisconnect={() => setDisconnectOpen(true)}
          oauthError={oauthError}
        />
        {setupReadiness !== null ? (
          <ActivationChecklist
            providerLabel={configuredProviderLabel}
            accountName={scrubBrand(descriptorData.connectedAccountName)}
            descriptor={descriptorData}
            readiness={setupReadiness}
            attention={attention}
            locationRef={locationRef}
            busy={busy}
            saving={saving}
            validating={validating}
            error={activationError}
            onSetChargeRules={openChargeRules}
            onSetPin={openChargeRules}
            onValidate={() => void validateConnection()}
            onActivate={activate}
            onFix={runActivationFix}
          />
        ) : null}
        {descriptorData.configured ? (
          <>
            <Divider sx={{ my: 2 }} />
            <ConfiguredConnection
              provider={selectedProvider}
              descriptor={descriptorData}
              status={status}
              statusLoading={statusLoading}
              webhookStatus={webhookStatus}
              webhookStatusLoading={webhookStatusLoading}
              webhookTestResult={webhookTestResult}
              webhookTestError={webhookTestError}
              sendingWebhookTest={sendingWebhookTest}
              webhookTestRateLimited={webhookTestRateLimited}
              environment={environment}
              setEnvironment={setEnvironment}
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              webhookValues={webhookValues}
              setWebhookValues={setWebhookValues}
              fieldErrors={fieldErrors}
              formError={formError}
              oauthError={oauthError}
              busy={busy}
              manualConversionMode={manualConversionMode}
              pspLocations={pspLocations}
              selectedPspLocation={selectedPspLocation}
              setSelectedPspLocation={setSelectedPspLocation}
              onSave={() => void saveManual()}
              onSavePsp={() => void savePspLocation()}
              onReconnect={() => void startOAuth()}
              onSendWebhookTest={() => void sendTestEvent()}
              onCancelManualConversion={() => {
                // Cancel abandons the manual switch and reverts to the saved
                // descriptor's OAuth baseline (restores the reconnect view;
                // clears any stale manual-form state) — DNKADM-0131.
                setManualConversionMode(false);
                seedFormFromConfiguredDescriptor(descriptorData);
              }}
            />
          </>
        ) : null}
        {/* DNKADM-0421: Apple Pay is a Square feature and needs a live connection to register sites. */}
        {descriptorData.configured && descriptorData.active && descriptorData.provider === 'square' ? (
          <>
            <Divider sx={{ my: 2 }} />
            <ApplePayRow key={locationValue} locationRef={locationRef} busy={busy} />
          </>
        ) : null}
      </Box>

      {replacingConfiguredProvider && selectedProvider !== null ? (
        <Alert severity="warning" sx={{ maxWidth: 760, mb: 2 }}>
          Replacing {providerLabel(null, descriptorData.provider)} with {selectedProvider.label} replaces stored
          credentials + requires re-validation.
        </Alert>
      ) : null}

      {selectedProvider !== null &&
      (descriptorData.configured === false || replacingConfiguredProvider) &&
      pickedMode === 'manual' ? (
        <Box ref={detailStartRef}>
          <ManualForm
            provider={selectedProvider}
            descriptor={replacingConfiguredProvider ? null : descriptorData}
            environment={environment}
            setEnvironment={setEnvironment}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            webhookValues={webhookValues}
            setWebhookValues={setWebhookValues}
            fieldErrors={fieldErrors}
            error={formError}
            busy={busy}
            onSubmit={() => void saveManual()}
          />
        </Box>
      ) : null}

      {/* Opened from the setup checklist and the activation error; stacked over the connection dialog. */}
      <PaymentsChargeRulesDialog
        open={chargeRulesOpen}
        onClose={() => setChargeRulesOpen(false)}
        locationRef={locationRef}
        provider={selectedProvider}
        descriptor={descriptorData}
        parentBusy={busy}
        elevated
        onSaved={async () => {
          // Close before the reload: the re-read swaps in the skeleton and back, and a still-open flag
          // would re-mount the dialog for a moment before the dialog's own onClose runs.
          setChargeRulesOpen(false);
          await reloadAfterChange();
        }}
      />

      <ChangeProviderDialog
        open={changeProviderOpen}
        registry={registry.data}
        configured={descriptorData.configured}
        configuredProviderId={configuredProviderId}
        currentProviderId={currentChoiceProviderId}
        locked={providerLocked}
        lockedProviderLabel={configuredProviderLabel}
        lockedAction={lockedProviderAction}
        onClose={() => setChangeProviderOpen(false)}
        onApplyProvider={chooseProvider}
      />

      <Dialog open={manualSwitchOpen} onClose={() => setManualSwitchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Switch to manual credentials?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching to manual credentials replaces the OAuth connection and requires re-validation.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setManualSwitchOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setManualSwitchOpen(false);
              setPickedMode('manual');
              setManualConversionMode(true);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <NonDismissableDialog open={redirecting} disableEscapeKeyDown>
        <DialogTitle>Preparing connection for {configuredProviderLabel ?? 'the provider'}…</DialogTitle>
        <DialogContent>
          <LinearProgress />
          <DialogContentText sx={{ mt: 1.5 }}>
            The secure connection page will open in the new tab.
          </DialogContentText>
        </DialogContent>
      </NonDismissableDialog>

      <Dialog open={disconnectOpen} onClose={() => setDisconnectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect OAuth access?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Disconnecting removes the OAuth grant from this payment connection. Reconnect with a fresh OAuth
            flow when access is needed again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDisconnectOpen(false)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => void disconnectOAuth()}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function LoadingPayments() {
  return (
    <Stack sx={{ gap: 2, maxWidth: 760 }}>
      <Skeleton variant="rounded" height={42} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" height={160} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" height={260} sx={{ borderRadius: 1 }} />
    </Stack>
  );
}

function NonDismissableDialog({
  open,
  disableEscapeKeyDown,
  children,
}: {
  open: boolean;
  disableEscapeKeyDown: boolean;
  children: ReactNode;
}) {
  const handleClose = useCallback(
    (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (disableEscapeKeyDown || reason === 'backdropClick') return;
    },
    [disableEscapeKeyDown],
  );

  return (
    <Dialog open={open} onClose={handleClose}>
      {children}
    </Dialog>
  );
}

function ProviderSummary({
  chosenProviderLabel,
  provider,
  descriptor,
  environment,
  setEnvironment,
  currency,
  disconnectGuidance,
  liveGrant,
  disconnectedEditable,
  pickedMode,
  busy,
  validating,
  deactivating,
  disconnecting,
  onOpenChangeProvider,
  onConnect,
  onCopyConnectLink,
  onEnterCredentials,
  onManualSwitch,
  onValidate,
  onDeactivate,
  onReconnect,
  onDisconnect,
  oauthError,
}: {
  chosenProviderLabel: string | null;
  provider: PaymentsProviderRegistry | null;
  descriptor: PaymentsConnectionDescriptor;
  environment: string;
  setEnvironment: (value: string) => void;
  currency: string;
  disconnectGuidance: string | null;
  liveGrant: boolean;
  disconnectedEditable: boolean;
  pickedMode: 'oauth' | 'manual' | null;
  busy: boolean;
  validating: boolean;
  deactivating: boolean;
  disconnecting: boolean;
  onOpenChangeProvider: () => void;
  onConnect: () => void;
  onCopyConnectLink: () => void;
  onEnterCredentials: () => void;
  onManualSwitch: () => void;
  onValidate: () => void;
  onDeactivate: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  oauthError: ConnectOAuthErrorView | null;
}) {
  const providerText = chosenProviderLabel ?? 'None';
  const environmentValue = descriptor.configured ? descriptor.environment : environment;
  const environmentField = provider === null ? null : connectionField(provider, 'environment');
  const storedEnvironmentMismatch =
    descriptor.configured &&
    environmentIsMismatch(
      descriptor.environment,
      environmentField,
      descriptor.environmentPermitted,
    );
  const environmentText =
    provider !== null &&
    (environmentValue === 'sandbox' || environmentIsChoice(provider) || storedEnvironmentMismatch)
      ? environmentLabel(environmentValue)
      : null;
  const currencyText = scrubBrand(descriptor.configured ? descriptor.currency : currency);
  const accountName = scrubBrand(descriptor.connectedAccountName);
  const deauthorized = descriptor.configured && isDeauthorizedOAuth(descriptor);
  const disconnectedStatus = deauthorized ? 'Not connected - reconnect required' : 'Not connected';
  const isSquare = provider?.provider === 'square';
  const statusWord = descriptor.active ? 'Active' : deauthorized ? 'Disconnected - reconnect required' : 'Inactive';
  const headerLines = [
    { label: 'Provider', value: providerText },
    { label: 'Environment', value: environmentText },
    { label: 'Currency', value: currencyText },
    { label: 'Status', value: liveGrant ? statusWord : disconnectedEditable ? disconnectedStatus : statusWord },
  ].filter((part): part is { label: string; value: string } => part.value !== null && part.value.length > 0);

  return (
    <Box>
      {!descriptor.configured && provider === null ? (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>Not connected</Typography>
          <Button variant="contained" onClick={onOpenChangeProvider} disabled={busy}>
            Choose provider
          </Button>
        </Stack>
      ) : null}

      {descriptor.configured || provider !== null ? (
        <Stack sx={{ gap: 0.75 }}>
          <Stack role="group" aria-label="Connection summary" sx={{ gap: 0.5 }}>
            {headerLines.map((line) => (
              <Stack key={line.label} direction="row" sx={{ alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ fontSize: 15, color: 'text.secondary' }}>
                  {line.label}:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: line.label === 'Status' && deauthorized ? 'warning.main' : 'text.primary',
                  }}
                >
                  {line.value}
                </Typography>
                {line.label === 'Provider' ? (
                  <>
                    {disconnectedEditable ? (
                      <Button size="small" variant="text" onClick={onOpenChangeProvider}>
                        Change provider
                      </Button>
                    ) : null}
                    {descriptor.configured && descriptor.active ? (
                      <Button size="small" color="error" onClick={onDeactivate} disabled={busy}>
                        {deactivating ? 'Deactivating...' : 'Deactivate'}
                      </Button>
                    ) : null}
                    {/* Activate lives in the setup checklist below the summary (DNKADM-0417 U1). */}
                  </>
                ) : null}
              </Stack>
            ))}
          </Stack>

          {liveGrant ? (
            <>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {accountName !== null ? (
                  <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Connected to: {accountName}</Typography>
                ) : null}
                <Button size="small" variant="text" onClick={onValidate} disabled={busy || validating}>
                  {validating ? 'Testing connection...' : 'Test connection'}
                </Button>
                {isOAuthManaged(descriptor) ? (
                  <>
                    <ConnectLinkActions busy={busy} onOpen={onReconnect} onCopy={onCopyConnectLink} />
                    <Button size="small" variant="text" color="error" onClick={onDisconnect} disabled={busy}>
                      {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                  </>
                ) : null}
              </Stack>
              {isOAuthManaged(descriptor) ? <ConnectLinkRiskCopy /> : null}
              {descriptor.validationStatus === 'invalid' ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Typography sx={{ fontSize: 12.5 }}>This connection isn&apos;t valid - test the connection.</Typography>
                </Alert>
              ) : null}
            </>
          ) : null}

          {disconnectedEditable ? (
            <Stack sx={{ gap: 0.75 }}>
              {provider !== null && pickedMode === 'oauth' ? (
                <Box role="group" aria-label="Connection environment">
                  <ConnectionSelect
                    provider={provider}
                    fieldKey="environment"
                    value={environment}
                    onChange={setEnvironment}
                    disabled={busy}
                    marginBottom={0}
                  />
                </Box>
              ) : null}
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {provider !== null && pickedMode === 'oauth' ? (
                  isSquare ? (
                    <SquareConnectOptions
                      busy={busy}
                      onOpen={onConnect}
                      onCopy={onCopyConnectLink}
                      onEnterCredentials={onEnterCredentials}
                    />
                  ) : (
                    <ConnectLinkActions busy={busy} onOpen={onConnect} onCopy={onCopyConnectLink} />
                  )
                ) : null}
                {provider !== null && pickedMode === 'manual' ? (
                  <Button variant="outlined" onClick={onEnterCredentials} disabled={busy}>
                    Enter credentials
                  </Button>
                ) : null}
                {provider !== null &&
                !isSquare &&
                provider.connectModes.includes('manual') &&
                pickedMode === 'oauth' ? (
                  <Button variant="text" color="inherit" onClick={onManualSwitch} disabled={busy}>
                    Switch to manual credentials
                  </Button>
                ) : null}
              </Stack>
              {provider !== null && pickedMode === 'oauth' ? <ConnectLinkRiskCopy /> : null}
              {deauthorized ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Selecting production re-authorizes this connection in production.
                </Typography>
              ) : null}
              {oauthError !== null ? <ConnectOAuthErrorAlert error={oauthError} onRetry={onConnect} /> : null}
            </Stack>
          ) : null}

          {storedEnvironmentMismatch ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              This connection was created in {environmentLabel(descriptor.environment)}, which is no longer available.
              Reconnecting will authorize it in {environmentLabel(environment)}.
            </Typography>
          ) : null}

          {disconnectGuidance !== null ? (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              {disconnectGuidance}
            </Alert>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

// Narrow test seam for render-gate combinations that the descriptor classifier cannot construct.
export const ProviderSummaryForTest = ProviderSummary;

function ConnectLinkActions({
  busy,
  onOpen,
  onCopy,
}: {
  busy: boolean;
  onOpen: () => void;
  onCopy: () => void;
}) {
  return (
    <>
      <Link
        component="button"
        type="button"
        role="link"
        onClick={onOpen}
        disabled={busy}
        sx={{ fontSize: 13 }}
      >
        Open connection in new tab
      </Link>
      <Button size="small" variant="text" onClick={onCopy} disabled={busy}>
        Copy connection link
      </Button>
    </>
  );
}

function SquareConnectOptions({
  busy,
  onOpen,
  onCopy,
  onEnterCredentials,
}: {
  busy: boolean;
  onOpen: () => void;
  onCopy: () => void;
  onEnterCredentials: () => void;
}) {
  return (
    <Stack role="group" aria-label="Square connection options" sx={{ width: '100%', gap: 1.25, mt: 0.5 }}>
      <Box
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Option 1 — Sign in first, then connect</Typography>
          <Chip label="Recommended" size="small" color="primary" />
        </Stack>
        <Stack component="ol" sx={{ mt: 1, mb: 0.75, pl: 2.5, gap: 0.75 }}>
          <Typography component="li" sx={{ fontSize: 13 }}>
            <Link href={SQUARE_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
              Open your Square dashboard
            </Link>
          </Typography>
          <Typography component="li" sx={{ fontSize: 13 }}>
            <Link component="button" type="button" role="link" onClick={onCopy} disabled={busy}>
              Copy your connection link to clipboard
            </Link>
          </Typography>
          <Typography component="li" sx={{ fontSize: 13 }}>
            Paste it into that tab&rsquo;s address bar and press Enter.
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Signing in to Square first keeps a sign-in or MFA prompt from interrupting the connection.
        </Typography>
      </Box>

      <Box sx={{ px: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Option 2 — Open the connection link directly</Typography>
        <Link
          component="button"
          type="button"
          role="link"
          onClick={onOpen}
          disabled={busy}
          sx={{ mt: 0.5, fontSize: 13 }}
        >
          Open connection in new tab
        </Link>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
          If Square asks you to sign in or complete MFA, this route can fail; use Option 1 instead.
        </Typography>
      </Box>

      <Box sx={{ px: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Option 3 — Connect manually</Typography>
        <Typography sx={{ mt: 0.25, fontSize: 12, color: 'text.secondary' }}>
          If you&rsquo;d rather enter your Square credentials yourself.
        </Typography>
        <Button size="small" variant="text" color="inherit" onClick={onEnterCredentials} disabled={busy}>
          Enter Square credentials
        </Button>
      </Box>
    </Stack>
  );
}

function ConnectLinkRiskCopy() {
  return (
    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
      Anyone with this link can connect a payment account for this tenant.
    </Typography>
  );
}

function ConnectOAuthErrorAlert({
  error,
  onRetry,
}: {
  error: ConnectOAuthErrorView;
  onRetry: () => void;
}) {
  return (
    <Alert
      severity="error"
      action={
        error.retryable ? (
          <Button size="small" color="inherit" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {error.message}
    </Alert>
  );
}

/**
 * The operator-locale default date and time, on the venue's clock (DNKADM-0476 F-05); null when
 * absent or unparseable.
 */
function formatDateTime(iso: string | null, timeZone: string | null): string | null {
  const ms = instantMs(iso);
  return ms === null ? null : formatInstantMs(ms, timeZone, {});
}

function credentialSourceLabel(source: PaymentsConnectionDescriptor['credentialSource']): string | null {
  if (source === null) return null;
  if (source === 'oauth') return 'OAuth';
  if (source === 'manual') return 'Manual';
  return scrubBrand(source);
}

function validationLabel(status: PaymentsConnectionDescriptor['validationStatus']): string {
  if (status === 'valid') return 'Valid';
  if (status === 'invalid') return 'Invalid';
  return 'Unvalidated';
}

function connectionDetailsValidation(descriptor: PaymentsConnectionDescriptor): string | null {
  const detail = scrubBrand(descriptor.detail);
  if (descriptor.validationStatus === 'valid' && detail === null) return null;
  const status = validationLabel(descriptor.validationStatus);
  return detail !== null ? `${status} · ${detail}` : status;
}

function chargeRuleSummary(descriptor: PaymentsConnectionDescriptor): string | null {
  const band = descriptor.currencies[0] ?? null;
  const currencyValue = scrubBrand(descriptor.currency ?? band?.currency ?? null);
  const parts: string[] = [];
  if (currencyValue !== null) parts.push(currencyValue);
  if (band !== null) {
    const min = formatMoneyMinor(band.minChargeMinor, band.currency);
    const max = formatMoneyMinor(band.maxChargeMinor, band.currency);
    if (min !== null && max !== null) parts.push(`${min} - ${max}`);
  }
  // DNKADM-0400: the console never reads the PIN, only whether one is set.
  if (descriptor.overridePinSet) parts.push('PIN Set');
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function PaymentsChargeRulesSummary({ descriptor }: { descriptor: PaymentsConnectionDescriptor }) {
  return <>{chargeRuleSummary(descriptor) ?? 'No charge rules set'}</>;
}

function savedChargeRulesFinePrint(descriptor: PaymentsConnectionDescriptor): string | null {
  const summary = chargeRuleSummary(descriptor);
  return summary === null ? null : `Saved charge rules kept: ${summary} - reapplied when you reconnect.`;
}

function DiagnosticFinePrint({
  descriptor,
}: {
  descriptor: PaymentsConnectionDescriptor;
}) {
  const calendarZone = useCalendarZone();
  const details: string[] = [];
  const credential = credentialSourceLabel(descriptor.credentialSource);
  const validation = connectionDetailsValidation(descriptor);
  const lastValidated = formatDateTime(descriptor.lastValidatedAt, calendarZone);
  const tokenExpires = formatDateTime(descriptor.oauthTokenExpiresAt, calendarZone);
  if (credential !== null) details.push(`Credential source: ${credential}`);
  if (validation !== null) details.push(`Validation: ${validation}`);
  if (lastValidated !== null) details.push(`Last validated: ${lastValidated}`);
  if (tokenExpires !== null) details.push(`OAuth token expires: ${tokenExpires}`);
  if (details.length === 0) return null;

  return (
    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.7 }}>
      {details.map((detail, index) => (
        <Box component="span" key={detail}>
          {index > 0 ? ' · ' : ''}
          {detail}
        </Box>
      ))}
    </Typography>
  );
}

export function PaymentsChargeRulesDialog({
  open,
  onClose,
  locationRef,
  provider,
  descriptor,
  parentBusy = false,
  loading: loadingDescriptor = false,
  loadError = null,
  providerUnavailable = false,
  onRetry,
  onSaved,
  elevated = false,
}: {
  open: boolean;
  onClose: () => void;
  locationRef: string | undefined;
  provider: PaymentsProviderRegistry | null;
  descriptor: PaymentsConnectionDescriptor | null;
  parentBusy?: boolean;
  loading?: boolean;
  loadError?: unknown | null;
  providerUnavailable?: boolean;
  onRetry?: () => void;
  onSaved: (descriptor: PaymentsConnectionDescriptor) => void | Promise<void>;
  elevated?: boolean;
}) {
  const showToast = useToast();
  const [currency, setCurrency] = useState('');
  const [currencyRows, setCurrencyRows] = useState<CurrencyBandFormRow[]>([]);
  const [overridePin, setOverridePin] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [oauthConfigReadOnly, setOauthConfigReadOnly] = useState(false);

  const seedEditor = useCallback(() => {
    if (provider === null || descriptor === null) return;
    const nextCurrency =
      provider.multiCurrency && descriptor.currencies.length > 0
        ? descriptor.currencies.find((row) => row.currency === descriptor.currency)?.currency ?? descriptor.currencies[0]!.currency
        : descriptor.currency ?? firstOption(connectionField(provider, 'currency'));
    setCurrency(nextCurrency);
    setCurrencyRows(seededCurrencyRows(provider, descriptor, nextCurrency));
    setOverridePin('');
    setFieldErrors({});
    setFormError(null);
    setOauthConfigReadOnly(false);
  }, [descriptor, provider]);

  useEffect(() => {
    if (open) seedEditor();
  }, [open, seedEditor]);

  function closeEditor() {
    if (saving) return;
    seedEditor();
    onClose();
  }

  async function save() {
    if (provider === null || descriptor === null) return;
    const errors = validateCurrencyGuards(provider, currencyRows, overridePin, descriptor);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    const body: PaymentsConnectionInput = { provider: provider.provider, active: descriptor.active };
    if (descriptor.environment !== null) body.environment = descriptor.environment;
    if (currency.length > 0) body.currency = currency;
    if (providerRequiresBands(provider)) attachCurrencyBands(body, currencyRows);
    const pinField = overridePinDescriptor(provider);
    if (pinField !== null && overridePin.trim().length > 0) body.overridePin = overridePin.trim();
    if (!isOAuthManaged(descriptor)) {
      const fields: Record<string, string> = {};
      for (const field of provider.descriptors) {
        if (isSecretField(field)) continue;
        const value = descriptor.fields[field.key];
        if (typeof value === 'string') fields[field.key] = value;
      }
      body.fields = fields;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const saved = await putPaymentsConnection(locationRef, body);
      showToast('Payment connection saved', 'success');
      await onSaved(saved);
      onClose();
    } catch (err) {
      if (v2ErrorCode(err) === 'OAUTH_MANAGED') setOauthConfigReadOnly(true);
      if (v2ErrorCode(err) === 'CURRENCY_BANDS_REQUIRED') {
        setFieldErrors({ currencies: 'Add at least one currency with min and max charge bands.' });
      } else if (v2ErrorCode(err) === 'OVERRIDE_PIN_REQUIRED') {
        const pinKey = overridePinDescriptor(provider)?.key ?? OVERRIDE_PIN_KEY;
        setFieldErrors({ [pinKey]: 'Set an Override PIN before activating.' });
      }
      const message = paymentErrorCopy(err);
      setFormError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const busy = parentBusy || saving;
  const loadedWithoutProvider =
    !loadingDescriptor && loadError === null && descriptor !== null && descriptor.provider === null;
  const recoverableProviderError =
    !loadingDescriptor && loadError === null && descriptor !== null && providerUnavailable;

  return (
    <Dialog
      open={open}
      onClose={closeEditor}
      aria-labelledby="payments-charge-rules-dialog-title"
      maxWidth="sm"
      fullWidth
      sx={elevated ? { zIndex: (theme) => theme.zIndex.modal + 1 } : undefined}
    >
      <DialogTitle id="payments-charge-rules-dialog-title">Charge rules</DialogTitle>
      <DialogContent dividers>
        {loadingDescriptor ? (
          <Stack role="status" aria-label="Loading charge rules" sx={{ gap: 1 }}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={120} />
          </Stack>
        ) : loadError !== null || recoverableProviderError ? (
          <Alert
            severity="error"
            action={
              onRetry ? (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              ) : null
            }
          >
            {loadError !== null
              ? errorMessage(loadError, 'Couldn’t load charge rules.')
              : 'Couldn’t load payment provider details.'}
          </Alert>
        ) : loadedWithoutProvider ? (
          <Typography sx={{ color: 'text.secondary' }}>Configure a payment provider before adding charge rules.</Typography>
        ) : provider !== null && descriptor !== null ? (
          <>
            {descriptor.currencies.length === 0 && !descriptor.overridePinSet ? (
              <Typography sx={{ mb: 2, color: 'text.secondary' }}>No charge rules set</Typography>
            ) : null}
            <CurrencyGuardFields
              provider={provider}
              descriptor={descriptor}
              currency={currency}
              setCurrency={setCurrency}
              currencyRows={currencyRows}
              setCurrencyRows={setCurrencyRows}
              overridePin={overridePin}
              setOverridePin={setOverridePin}
              fieldErrors={fieldErrors}
              disabled={busy || oauthConfigReadOnly}
              readOnly={oauthConfigReadOnly}
              compact
            />
            {oauthConfigReadOnly ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                Currency guards are read-only until this OAuth edit route is available.
              </Alert>
            ) : null}
            {formError !== null ? <Alert severity="error">{formError}</Alert> : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={closeEditor} disabled={saving}>
          {provider !== null && descriptor !== null && loadError === null && !providerUnavailable ? 'Cancel' : 'Close'}
        </Button>
        {provider !== null && descriptor !== null && loadError === null && !providerUnavailable ? (
          <Button variant="contained" onClick={() => void save()} disabled={busy}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function providerBlurb(providerId: string): string {
  switch (providerId) {
    case 'square':
      return 'Card payments and terminals';
    case 'stripe':
      return 'Online card processing';
    case 'tilopay':
      return 'Multi-currency, Costa Rica';
    default:
      return '';
  }
}

function ChangeProviderDialog({
  open,
  registry,
  configured,
  configuredProviderId,
  currentProviderId,
  locked,
  lockedProviderLabel,
  lockedAction,
  onClose,
  onApplyProvider,
}: {
  open: boolean;
  registry: PaymentsRegistry;
  configured: boolean;
  configuredProviderId: string | null;
  currentProviderId: string | null;
  locked: boolean;
  lockedProviderLabel: string;
  lockedAction: ProviderSummaryAction;
  onClose: () => void;
  onApplyProvider: (provider: PaymentsProviderRegistry) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const pm = resolvePaymentsMaps(theme);
  const enabledProviders = registry.providers.filter((provider) => provider.enabled);
  const [draftProviderId, setDraftProviderId] = useState(currentProviderId ?? '');
  const [confirmingReplace, setConfirmingReplace] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraftProviderId(currentProviderId ?? '');
    setConfirmingReplace(false);
  }, [currentProviderId, open]);

  const draftProvider =
    enabledProviders.find((provider) => provider.provider === draftProviderId) ?? null;
  const replacingConfiguredProvider =
    configured &&
    configuredProviderId !== null &&
    draftProvider !== null &&
    draftProvider.provider !== configuredProviderId;

  function closeDialog() {
    setConfirmingReplace(false);
    onClose();
  }

  function continueChoice() {
    if (draftProvider === null) return;
    if (replacingConfiguredProvider && !confirmingReplace) {
      setConfirmingReplace(true);
      return;
    }
    onApplyProvider(draftProvider);
    closeDialog();
  }

  if (locked) {
    return (
      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Change payment provider</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {lockedProviderLabel} is connected. Disconnect the current connection before switching providers — you
            can&apos;t run two providers at once.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            variant={lockedAction.variant ?? 'contained'}
            color={lockedAction.color ?? 'primary'}
            disabled={lockedAction.disabled}
            onClick={lockedAction.onClick}
          >
            {lockedAction.label}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Choose payment provider</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Choose the provider this payment connection should use.
        </DialogContentText>
        <Stack sx={{ gap: 1 }}>
          {enabledProviders.map((provider) => {
            const selected = provider.provider === draftProviderId;
            const blurb = providerBlurb(provider.provider);
            return (
              <Box
                key={provider.provider}
                component="button"
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  setDraftProviderId(provider.provider);
                  setConfirmingReplace(false);
                }}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  p: 1.5,
                  border: '1px solid',
                  borderColor:
                    selected ? 'primary.main' : theme.palette.mode === 'dark' ? t.lineStrong : 'divider',
                  borderRadius: 1,
                  bgcolor: selected ? pm.providerSelected.bg : t.surface,
                  color: 'text.primary',
                  cursor: 'pointer',
                  font: 'inherit',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: selected ? pm.providerSelected.hoverBg : inkAlpha(theme, 0.02),
                  },
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{provider.label}</Typography>
                {blurb.length > 0 ? (
                  <Typography sx={{ mt: 0.25, fontSize: 12.5, color: 'text.secondary' }}>
                    {blurb}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Stack>
        {confirmingReplace ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>Replace payment provider?</AlertTitle>
            Saving a different provider replaces stored credentials + requires re-validation.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={closeDialog}>
          Cancel
        </Button>
        <Button variant="contained" onClick={continueChoice} disabled={draftProvider === null}>
          {confirmingReplace ? 'Replace' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfiguredConnection(props: {
  provider: PaymentsProviderRegistry | null;
  descriptor: PaymentsConnectionDescriptor;
  status: PaymentsStatus | null;
  statusLoading: boolean;
  webhookStatus: PaymentsWebhookStatus;
  webhookStatusLoading: boolean;
  webhookTestResult: PaymentsWebhookTestResult | null;
  webhookTestError: string | null;
  sendingWebhookTest: boolean;
  webhookTestRateLimited: boolean;
  environment: string;
  setEnvironment: (value: string) => void;
  fieldValues: Record<string, string>;
  setFieldValues: (value: Record<string, string>) => void;
  webhookValues: Record<string, string>;
  setWebhookValues: (value: Record<string, string>) => void;
  fieldErrors: ValidationErrors;
  formError: string | null;
  oauthError: ConnectOAuthErrorView | null;
  busy: boolean;
  manualConversionMode: boolean;
  pspLocations: LoadState<PaymentsPspLocation[]>;
  selectedPspLocation: string;
  setSelectedPspLocation: (value: string) => void;
  onSave: () => void;
  onSavePsp: () => void;
  onReconnect: () => void;
  onSendWebhookTest: () => void;
  onCancelManualConversion: () => void;
}) {
  const {
    provider,
    descriptor,
    status,
    statusLoading,
    webhookStatus,
    webhookStatusLoading,
    webhookTestResult,
    webhookTestError,
    sendingWebhookTest,
    webhookTestRateLimited,
    environment,
    setEnvironment,
    fieldValues,
    setFieldValues,
    webhookValues,
    setWebhookValues,
    fieldErrors,
    formError,
    oauthError,
    busy,
    manualConversionMode,
    pspLocations,
    selectedPspLocation,
    setSelectedPspLocation,
    onSave,
    onSavePsp,
    onReconnect,
    onSendWebhookTest,
    onCancelManualConversion,
  } = props;
  const oauth = isOAuthManaged(descriptor);
  const deauthorized = isDeauthorizedOAuth(descriptor);
  const configuredManual = provider !== null && !oauth && !manualConversionMode;
  const locationPickerRef = useRef<HTMLDivElement | null>(null);
  const savedRules = deauthorized ? savedChargeRulesFinePrint(descriptor) : null;

  return (
    <Stack sx={{ gap: 2 }}>
      {descriptor.active ? (
        <Stack sx={{ gap: 0.75 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <HealthChip status={status} loading={statusLoading} />
            <WebhookHealthChip status={webhookStatus} loading={webhookStatusLoading} />
          </Stack>
          <WebhookDiagnostics status={webhookStatus} />
        </Stack>
      ) : null}

      <WebhookObservability
        provider={provider}
        available={descriptor.active && webhookStatus.available}
        busy={busy}
        sending={sendingWebhookTest}
        rateLimited={webhookTestRateLimited}
        result={webhookTestResult}
        error={webhookTestError}
        onSend={onSendWebhookTest}
      />

      {descriptor.locationSelectionRequired ? (
        <Box ref={locationPickerRef} id={PROVIDER_LOCATION_ANCHOR_ID}>
          <PspLocationPicker
            pspLocations={pspLocations}
            selected={selectedPspLocation}
            setSelected={setSelectedPspLocation}
            busy={busy}
            onSave={onSavePsp}
          />
        </Box>
      ) : null}

      {oauth && provider !== null && manualConversionMode ? (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Saving manual credentials replaces the OAuth connection and requires re-validation.
          </Alert>
          <ManualForm
            provider={provider}
            descriptor={null}
            environment={environment}
            setEnvironment={setEnvironment}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            webhookValues={webhookValues}
            setWebhookValues={setWebhookValues}
            fieldErrors={fieldErrors}
            error={formError}
            busy={busy}
            onSubmit={onSave}
          />
          <Button color="inherit" onClick={onCancelManualConversion} disabled={busy} sx={{ mt: 1 }}>
            Cancel manual switch
          </Button>
        </Box>
      ) : null}

      {configuredManual ? (
        <ManualForm
          provider={provider}
          descriptor={descriptor}
          environment={environment}
          setEnvironment={setEnvironment}
          fieldValues={fieldValues}
          setFieldValues={setFieldValues}
          webhookValues={webhookValues}
          setWebhookValues={setWebhookValues}
          fieldErrors={fieldErrors}
          error={null}
          busy={busy}
          settingsMode="credentialsOnly"
          onSubmit={onSave}
        />
      ) : null}

      {savedRules !== null ? (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{savedRules}</Typography>
      ) : null}

      {formError !== null ? <Alert severity="error">{formError}</Alert> : null}
      {oauthError !== null && !isDeauthorizedOAuth(descriptor) ? (
        <ConnectOAuthErrorAlert error={oauthError} onRetry={onReconnect} />
      ) : null}
      <DiagnosticFinePrint descriptor={descriptor} />
    </Stack>
  );
}

function yesNo(value: boolean): string {
  return value ? 'yes' : 'no';
}

function statLabel(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

function webhookVerdictLabel(verdict: string): string {
  switch (verdict) {
    case 'healthy':
      return 'healthy';
    case 'no_traffic':
      return 'new - no traffic yet';
    case 'attention':
      return 'attention';
    case 'misconfigured':
      return 'misconfigured';
    case 'unconfigured':
      return 'unconfigured';
    default:
      return verdict;
  }
}

function webhookVerdictColor(verdict: string): ChipProps['color'] {
  switch (verdict) {
    case 'healthy':
      return 'success';
    case 'attention':
      return 'warning';
    case 'misconfigured':
      return 'error';
    default:
      return 'default';
  }
}

function attentionSeverity(severity: string): AlertColor {
  const normalized = severity.toLowerCase();
  if (['critical', 'error', 'high', 'misconfigured'].includes(normalized)) return 'error';
  if (['warning', 'attention', 'medium'].includes(normalized)) return 'warning';
  return 'info';
}

function worstAttentionSeverity(severities: string[]): AlertColor {
  if (severities.some((severity) => attentionSeverity(severity) === 'error')) return 'error';
  if (severities.some((severity) => attentionSeverity(severity) === 'warning')) return 'warning';
  return 'info';
}

function attentionCountSummary(counts: Extract<PaymentsAttention, { available: true }>['counts']): string {
  const bySeverity = Object.entries(counts.bySeverity).map(([key, value]) => `${key}: ${value}`);
  const byType = Object.entries(counts.byType).map(([key, value]) => `${key}: ${value}`);
  const parts = [...bySeverity, ...byType];
  return parts.length > 0 ? parts.join(' · ') : 'Attention items are present.';
}

function AttentionStrip({ attention }: { attention: PaymentsAttention }) {
  if (!attention.available || attention.items.length === 0) return null;
  const severity = worstAttentionSeverity(attention.items.map((item) => item.severity));
  return (
    <Alert severity={severity} sx={{ mb: 2.5 }}>
      <AlertTitle sx={{ fontWeight: 600 }}>Payments need attention</AlertTitle>
      <Typography sx={{ fontSize: 12.5, mb: 0.75 }}>{attentionCountSummary(attention.counts)}</Typography>
      <Stack component="ul" sx={{ m: 0, pl: 2, gap: 0.75 }}>
        {attention.items.map((item, index) => (
          <Box component="li" key={`${item.type}-${item.since ?? index}`}>
            <Typography sx={{ fontSize: 12.5 }}>
              <strong>{item.type}</strong> · {item.severity}
              {item.since !== null ? ` · since ${item.since}` : ''}
              {item.locationRef !== null ? ` · location ${item.locationRef}` : ''}
            </Typography>
            {item.detail !== null ? (
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{item.detail}</Typography>
            ) : null}
            {item.refs.length > 0 ? (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Refs: {item.refs.join(', ')}</Typography>
            ) : null}
          </Box>
        ))}
      </Stack>
    </Alert>
  );
}

type PaymentsAttentionEntry = Extract<PaymentsAttention, { available: true }>['items'][number];

/**
 * pay-api's `connection_attention` item for THIS connection ("configured and valid but not active")
 * is exactly what the setup checklist says in owner words, so it moves under the checklist's
 * Diagnostics. Anything else in the strip (other locations, webhook failures) stays visible.
 */
/**
 * pay-api stores and reports the tenant-default connection under the location sentinel `"-"`
 * (`PaymentConnection.DEFAULT_LOCATION_SENTINEL`); its attention items carry `locationRef: "-"`, and the
 * "configured and valid but not active" reason is raised only for that row (`AttentionService`
 * `healthyButInactiveDefault`). A null ref is accepted too, as the parser's "absent" value.
 */
function isDefaultLocationRef(itemLocationRef: string | null): boolean {
  return itemLocationRef === null || itemLocationRef === '-';
}

function isSetupAttention(item: PaymentsAttentionEntry, locationRef: string | undefined): boolean {
  if (item.type !== 'connection_attention') return false;
  return locationRef === undefined ? isDefaultLocationRef(item.locationRef) : item.locationRef === locationRef;
}

function setupAttentionItems(attention: PaymentsAttention, locationRef: string | undefined): PaymentsAttentionEntry[] {
  return attention.available ? attention.items.filter((item) => isSetupAttention(item, locationRef)) : [];
}

function withoutSetupAttention(attention: PaymentsAttention, locationRef: string | undefined): PaymentsAttention {
  if (!attention.available) return attention;
  const items = attention.items.filter((item) => !isSetupAttention(item, locationRef));
  if (items.length === attention.items.length) return attention;
  const byType: Record<string, number> = {};
  const bySeverity: Record<string, number> = {};
  for (const item of items) {
    byType[item.type] = (byType[item.type] ?? 0) + 1;
    bySeverity[item.severity] = (bySeverity[item.severity] ?? 0) + 1;
  }
  return { available: true, counts: { byType, bySeverity }, items };
}

function bandSummary(descriptor: PaymentsConnectionDescriptor): string | null {
  const band = descriptor.currencies[0] ?? null;
  if (band === null) return null;
  const min = formatMoneyMinor(band.minChargeMinor, band.currency);
  const max = formatMoneyMinor(band.maxChargeMinor, band.currency);
  return min !== null && max !== null ? `${band.currency} · ${min} - ${max}` : band.currency;
}

function SetupStep({
  done,
  label,
  detail,
  action,
}: {
  done: boolean;
  label: string;
  detail: string | null;
  action?: { label: string; onClick: () => void; disabled: boolean } | undefined;
}) {
  return (
    <Stack component="li" direction="row" sx={{ gap: 1.25, alignItems: 'flex-start', py: 0.75 }}>
      <Icon
        name={done ? 'check_circle' : 'radio_button_unchecked'}
        sx={{ fontSize: 20, mt: 0.25, color: done ? 'success.main' : 'text.disabled' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
          <Chip
            size="small"
            variant="outlined"
            color={done ? 'success' : 'default'}
            label={done ? 'Done' : 'To do'}
          />
        </Stack>
        {detail !== null ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{detail}</Typography>
        ) : null}
      </Box>
      {action ? (
        <Button size="small" variant="outlined" onClick={action.onClick} disabled={action.disabled}>
          {action.label}
        </Button>
      ) : null}
    </Stack>
  );
}

/**
 * DNKADM-0417 U1 — the ordered steps between "connected" and "taking payments", each with its state
 * and a button to the place that finishes it. Activate is enabled only once every step is done.
 */
function ActivationChecklist({
  providerLabel: provider,
  accountName,
  descriptor,
  readiness,
  attention,
  locationRef,
  busy,
  saving,
  validating,
  error,
  onSetChargeRules,
  onSetPin,
  onValidate,
  onActivate,
  onFix,
}: {
  providerLabel: string;
  accountName: string | null;
  descriptor: PaymentsConnectionDescriptor;
  readiness: ActivationReadiness;
  attention: PaymentsAttention;
  locationRef: string | undefined;
  busy: boolean;
  saving: boolean;
  validating: boolean;
  error: ActivationErrorView | null;
  onSetChargeRules: () => void;
  onSetPin: () => void;
  onValidate: () => void;
  onActivate: () => void;
  onFix: (fix: ActivationFix) => void;
}) {
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const rawItems = setupAttentionItems(attention, locationRef);
  const ready = !readiness.activationBlocked && !readiness.missingCurrencyBands && !readiness.missingOverridePin;

  return (
    <Box
      role="group"
      aria-label="Payments setup"
      data-testid="payments-setup-checklist"
      sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Finish setting up payments</Typography>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0 }}>
        <SetupStep done label={`Connected to ${provider}`} detail={accountName} />
        {readiness.requiresValidation ? (
          <SetupStep
            done={false}
            label="Test the connection"
            detail="Checks that the provider accepts this connection."
            action={{ label: validating ? 'Testing...' : 'Run test', onClick: onValidate, disabled: busy }}
          />
        ) : null}
        {descriptor.locationSelectionRequired ? (
          <SetupStep
            done={false}
            label="Choose a provider location"
            detail="Pick the location that takes payments, below."
          />
        ) : null}
        {readiness.guardsAvailable ? (
          <>
            <SetupStep
              done={!readiness.missingCurrencyBands}
              label="Charge rules"
              detail={
                readiness.missingCurrencyBands
                  ? "The smallest and largest single charge we'll allow. Protects against typos like $10,000 instead of $100."
                  : bandSummary(descriptor)
              }
              action={
                readiness.missingCurrencyBands
                  ? { label: 'Set charge rules', onClick: onSetChargeRules, disabled: busy }
                  : undefined
              }
            />
            <SetupStep
              done={!readiness.missingOverridePin}
              label="Override PIN"
              detail={
                readiness.missingOverridePin
                  ? 'Staff enter this to approve a charge outside your charge rules.'
                  : 'PIN set'
              }
              action={
                readiness.missingOverridePin ? { label: 'Set PIN', onClick: onSetPin, disabled: busy } : undefined
              }
            />
          </>
        ) : null}
      </Box>
      <Stack direction="row" sx={{ mt: 1, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={onActivate} disabled={busy || !ready}>
          Activate payments
        </Button>
        {!ready ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Finish the steps above to turn payments on.</Typography>
        ) : null}
        {saving ? <CircularProgress size={18} aria-label="Saving" /> : null}
      </Stack>
      {error !== null ? <ActivationErrorAlert error={error} busy={busy} onFix={onFix} /> : null}
      {rawItems.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setDiagnosticsOpen((prev) => !prev)}
            aria-expanded={diagnosticsOpen}
            endIcon={<Icon name={diagnosticsOpen ? 'expand_less' : 'expand_more'} sx={{ fontSize: 18 }} />}
            sx={{ px: 0.5, minWidth: 0, fontSize: 12.5, color: 'text.secondary' }}
          >
            Diagnostics
          </Button>
          <Collapse in={diagnosticsOpen} unmountOnExit>
            <Stack sx={{ gap: 0.25, pt: 0.5 }}>
              {rawItems.map((item, index) => (
                <WebhookDiagnosticRow
                  key={`${item.type}-${item.since ?? index}`}
                  label={`${item.type} · ${item.severity}`}
                  value={item.detail ?? '—'}
                />
              ))}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}

/** DNKADM-0417 U2 — owner copy, a button to the fix, the request id small at the end. */
function ActivationErrorAlert({
  error,
  busy,
  onFix,
}: {
  error: ActivationErrorView;
  busy: boolean;
  onFix: (fix: ActivationFix) => void;
}) {
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const fix = error.fix;
  return (
    <Alert
      severity="error"
      sx={{ mt: 1.5 }}
      action={
        fix !== null ? (
          <Button color="inherit" size="small" onClick={() => onFix(fix)} disabled={busy}>
            {ACTIVATION_FIX_LABEL[fix]}
          </Button>
        ) : undefined
      }
    >
      <Typography sx={{ fontSize: 14 }}>{error.message}</Typography>
      {error.diagnostics !== null ? (
        <Box>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setDiagnosticsOpen((prev) => !prev)}
            aria-expanded={diagnosticsOpen}
            sx={{ px: 0, minWidth: 0, fontSize: 11.5, color: 'text.secondary' }}
          >
            Diagnostics
          </Button>
          <Collapse in={diagnosticsOpen} unmountOnExit>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontFamily: MONO }}>{error.diagnostics}</Typography>
          </Collapse>
        </Box>
      ) : null}
      {error.requestId !== null ? (
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Request {error.requestId}</Typography>
      ) : null}
    </Alert>
  );
}

function WebhookHealthChip({
  status,
  loading,
}: {
  status: PaymentsWebhookStatus;
  loading: boolean;
}) {
  if (loading) return <Chip size="small" label="Checking webhook..." />;
  if (!status.available) return null;
  return (
    <Tooltip title={<WebhookStatsTooltip stats={status.stats} />}>
      <Chip
        size="small"
        label={`Webhook: ${webhookVerdictLabel(status.verdict)}`}
        color={webhookVerdictColor(status.verdict)}
      />
    </Tooltip>
  );
}

function WebhookStatsTooltip({ stats }: { stats: Record<string, unknown> }) {
  const entries = Object.entries(stats)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .slice(0, 6);
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>7-day webhook stats</Typography>
      {entries.length === 0 ? (
        <Typography sx={{ fontSize: 12 }}>No 7-day stats yet</Typography>
      ) : (
        entries.map(([key, value]) => (
          <Typography key={key} sx={{ fontSize: 12 }}>
            {statLabel(key)}: {String(value)}
          </Typography>
        ))
      )}
    </Box>
  );
}

function WebhookDiagnostics({ status }: { status: PaymentsWebhookStatus }) {
  const [open, setOpen] = useState(false);
  const calendarZone = useCalendarZone();
  if (!status.available) return null;

  const reason = webhookStatusReason(status);
  const providerSide = status.providerSide;
  const inbound = status.inbound;
  const hasRawDiagnostics = providerSide !== null || inbound !== null;
  if (reason === null && !hasRawDiagnostics) return null;

  const reasonColor = status.verdict === 'misconfigured' ? 'error.main' : 'warning.main';

  return (
    <Stack sx={{ gap: 0.5 }}>
      {reason !== null ? (
        <Typography sx={{ fontSize: 12.5, color: reasonColor }}>
          {reason.text}
          {reason.detail !== null ? (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' — '}
              {reason.detail}
            </Box>
          ) : null}
        </Typography>
      ) : null}

      {hasRawDiagnostics ? (
        <Box>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            endIcon={<Icon name={open ? 'expand_less' : 'expand_more'} sx={{ fontSize: 18 }} />}
            sx={{ px: 0.5, minWidth: 0, fontSize: 12.5, color: 'text.secondary' }}
          >
            Diagnostics
          </Button>
          <Collapse in={open} unmountOnExit>
            <Stack sx={{ gap: 0.25, pt: 0.5 }}>
              {providerSide !== null ? (
                <>
                  <WebhookDiagnosticRow label="Provider checked" value={yesNo(providerSide.checked)} />
                  <WebhookDiagnosticRow
                    label="Subscription found"
                    value={yesNo(providerSide.subscriptionFound)}
                  />
                  <WebhookDiagnosticRow
                    label="Matches expected"
                    value={yesNo(providerSide.matchesExpected)}
                  />
                  <WebhookDiagnosticRow label="Status" value={providerSide.status ?? '—'} />
                  <WebhookDiagnosticRow label="Registered URL" value={providerSide.url ?? '—'} />
                  <WebhookDiagnosticRow
                    label="Registered events"
                    value={providerSide.eventTypes.length > 0 ? providerSide.eventTypes.join(', ') : '—'}
                  />
                  <WebhookDiagnosticRow label="Note" value={providerSide.note ?? '—'} />
                </>
              ) : null}
              {inbound !== null ? (
                <>
                  <WebhookDiagnosticRow
                    label="Signature failures (7d)"
                    value={inbound.signatureFailures7d === null ? '—' : String(inbound.signatureFailures7d)}
                  />
                  <WebhookDiagnosticRow
                    label="Last signature failure"
                    value={formatDateTime(inbound.lastSignatureFailureAt, calendarZone) ?? '—'}
                  />
                  <WebhookDiagnosticRow
                    label="Oldest unresolved failure"
                    value={formatDateTime(inbound.oldestUnresolvedFailureAt, calendarZone) ?? '—'}
                  />
                </>
              ) : null}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Stack>
  );
}

function WebhookDiagnosticRow({ label, value }: { label: string; value: string }) {
  return (
    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.7 }}>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{' '}
      {value}
    </Typography>
  );
}

const APPLE_PAY_CHIP: Record<PaymentsApplePayStatus, { label: string; color: ChipProps['color'] }> = {
  VERIFIED: { label: 'Verified', color: 'success' },
  PENDING: { label: 'Pending', color: 'warning' },
  FAILED: { label: 'Failed', color: 'error' },
};

function applePayDetail(site: PaymentsApplePayDomain): string {
  if (site.status === 'VERIFIED') return 'Apple Pay is on for this site';
  if (site.status === 'PENDING') return 'Square is still verifying this site. Check again in a few minutes';
  return scrubBrand(site.message) ?? 'Square could not verify this site';
}

/**
 * DNKADM-0421: registers the connection's sites for Apple Pay (DNKAPI-0597) and shows each site's
 * verdict. The result lives in this component only; reopening the page offers the button again.
 */
function ApplePayRow({ locationRef, busy }: { locationRef: string | undefined; busy: boolean }) {
  const [running, setRunning] = useState(false);
  const [sites, setSites] = useState<PaymentsApplePayDomain[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      setSites(await setUpApplePay(locationRef));
    } catch (err) {
      setError(errorMessage(err, 'Apple Pay could not be set up. Try again.'));
    } finally {
      setRunning(false);
    }
  }

  const checked = sites !== null;
  return (
    <Box role="group" aria-label="Apple Pay">
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, alignItems: { sm: 'center' } }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Apple Pay</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            Let customers pay with Apple Pay on your booking sites.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => void run()}
          disabled={busy || running}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {running ? 'Checking...' : checked ? 'Check again' : 'Set up Apple Pay'}
        </Button>
      </Stack>
      {error !== null ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      {sites !== null && error === null ? (
        sites.length === 0 ? (
          <Typography sx={{ mt: 1.5, fontSize: 12.5, color: 'text.secondary' }}>
            No sites were found to set up.
          </Typography>
        ) : (
          <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5, gap: 1 }}>
            {sites.map((site) => (
              <Stack
                component="li"
                key={site.domainName}
                direction="row"
                sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
              >
                <Typography sx={{ fontSize: 13, fontFamily: MONO }}>{site.domainName}</Typography>
                <Chip size="small" label={APPLE_PAY_CHIP[site.status].label} color={APPLE_PAY_CHIP[site.status].color} />
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{applePayDetail(site)}</Typography>
              </Stack>
            ))}
          </Stack>
        )
      ) : null}
    </Box>
  );
}

function WebhookObservability({
  provider,
  available,
  busy,
  sending,
  rateLimited,
  result,
  error,
  onSend,
}: {
  provider: PaymentsProviderRegistry | null;
  available: boolean;
  busy: boolean;
  sending: boolean;
  rateLimited: boolean;
  result: PaymentsWebhookTestResult | null;
  error: string | null;
  onSend: () => void;
}) {
  if (!available) return null;
  const helpText = provider?.webhookFields.map((field) => field.helpText).find((text) => text !== null) ?? null;
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, alignItems: { sm: 'center' } }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Webhook test event</Typography>
          {helpText !== null ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{helpText}</Typography>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          onClick={onSend}
          disabled={busy || sending || rateLimited}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Icon name="send" />}
        >
          {sending ? 'Sending...' : 'Send test event'}
        </Button>
      </Stack>
      {error !== null ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      {result !== null && result.available ? <WebhookTestResultView result={result} /> : null}
    </Box>
  );
}

function WebhookTestResultView({ result }: { result: Exclude<PaymentsWebhookTestResult, { available: false }> }) {
  if (result.kind === 'roundTrip') {
    return (
      <Stack direction="row" sx={{ mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`Sent: ${yesNo(result.sent)}`} color={result.sent ? 'success' : 'error'} />
        <Chip
          size="small"
          label={`Received: ${yesNo(result.received)}`}
          color={result.received ? 'success' : 'warning'}
        />
        <Chip
          size="small"
          label={`Signature: ${result.signatureOk ? 'ok' : 'failed'}`}
          color={result.signatureOk ? 'success' : 'error'}
        />
        <Chip size="small" label={`Latency: ${result.latencyMs === null ? 'n/a' : `${result.latencyMs}ms`}`} />
      </Stack>
    );
  }
  if (result.kind === 'instructions') {
    return (
      <Typography sx={{ mt: 1.5, fontFamily: MONO, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>
        {result.instructions}
      </Typography>
    );
  }
  if (result.kind === 'detail') {
    return (
      <Typography sx={{ mt: 1.5, fontSize: 12.5, color: 'text.secondary' }}>{result.detail}</Typography>
    );
  }
  return result.detail !== null ? (
    <Typography sx={{ mt: 1.5, fontSize: 12.5, color: 'text.secondary' }}>{result.detail}</Typography>
  ) : null;
}

function ManualForm({
  provider,
  descriptor,
  environment,
  setEnvironment,
  fieldValues,
  setFieldValues,
  webhookValues,
  setWebhookValues,
  fieldErrors,
  error,
  busy,
  settingsMode = 'full',
  onSubmit,
}: {
  provider: PaymentsProviderRegistry;
  descriptor: PaymentsConnectionDescriptor | null;
  environment: string;
  setEnvironment: (value: string) => void;
  fieldValues: Record<string, string>;
  setFieldValues: (value: Record<string, string>) => void;
  webhookValues: Record<string, string>;
  setWebhookValues: (value: Record<string, string>) => void;
  fieldErrors: ValidationErrors;
  error: string | null;
  busy: boolean;
  settingsMode?: 'full' | 'credentialsOnly';
  onSubmit: () => void;
}) {
  return (
    <Box sx={{ maxWidth: 620 }}>
      {settingsMode === 'full' ? (
        <>
          {environmentIsChoice(provider) ? (
            <Stack
              data-testid="payments-environment-row"
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ gap: 2 }}
            >
              <ConnectionSelect
                provider={provider}
                fieldKey="environment"
                value={environment}
                onChange={setEnvironment}
                disabled={busy}
              />
            </Stack>
          ) : null}
        </>
      ) : null}
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 2 }}>
        {settingsMode === 'full'
          ? 'Changing environment or rotating a saved secret resets validation - re-test the connection.'
          : 'Rotating a saved secret resets validation - re-test the connection.'}
      </Typography>

      {provider.descriptors.map((field) => (
        <GeneratedField
          key={field.key}
          field={field}
          value={fieldValues[field.key] ?? ''}
          onChange={(value) => setFieldValues({ ...fieldValues, [field.key]: value })}
          saved={descriptor !== null && secretPresence(descriptor, field.key)}
          error={fieldErrors[field.key]}
          disabled={busy}
        />
      ))}

      <NotificationUrl url={descriptor?.webhook.notificationUrl ?? providerTemplate(descriptor, provider.provider)} />

      {provider.webhookFields.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          {provider.webhookFields.map((field) => (
            <GeneratedField
              key={field.key}
              field={field}
              value={webhookValues[field.key] ?? ''}
              onChange={(value) => setWebhookValues({ ...webhookValues, [field.key]: value })}
              saved={descriptor?.webhook.secretSet === true}
              error={fieldErrors[field.key]}
              disabled={busy}
            />
          ))}
        </Box>
      ) : null}

      {error !== null ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Button variant="contained" onClick={onSubmit} disabled={busy}>
        {busy ? 'Saving…' : 'Save connection'}
      </Button>
    </Box>
  );
}

function ConnectionSelect({
  provider,
  fieldKey,
  value,
  onChange,
  disabled,
  marginBottom = 2,
}: {
  provider: PaymentsProviderRegistry;
  fieldKey: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  marginBottom?: number;
}) {
  const field = connectionField(provider, fieldKey);
  if (field === null || (fieldKey === 'environment' && field.options.length < 2)) return null;
  return (
    <TextField
      select={field.type === 'select'}
      size="small"
      label={field.label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      helperText={field.helpText ?? undefined}
      sx={{ minWidth: fieldKey === 'environment' ? 110 : 160, mb: marginBottom }}
      slotProps={{ htmlInput: { 'aria-label': field.label } }}
    >
      {field.type === 'select'
        ? field.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.value === 'sandbox' ? SANDBOX_ENVIRONMENT_LABEL : option.label}
            </MenuItem>
          ))
        : null}
    </TextField>
  );
}

function CurrencyGuardFields({
  provider,
  descriptor,
  currency,
  setCurrency,
  currencyRows,
  setCurrencyRows,
  overridePin,
  setOverridePin,
  fieldErrors,
  disabled,
  readOnly = false,
  compact = false,
}: {
  provider: PaymentsProviderRegistry;
  descriptor: PaymentsConnectionDescriptor | null;
  currency: string;
  setCurrency: (value: string) => void;
  currencyRows: CurrencyBandFormRow[];
  setCurrencyRows: (value: CurrencyBandFormRow[]) => void;
  overridePin: string;
  setOverridePin: (value: string) => void;
  fieldErrors: ValidationErrors;
  disabled: boolean;
  readOnly?: boolean;
  compact?: boolean;
}) {
  const pinField = overridePinDescriptor(provider);
  const options = currencyOptions(provider);
  const selectedCurrencies = new Set(currencyRows.map((row) => row.currency).filter(Boolean));
  const primaryOptions = provider.multiCurrency
    ? currencyRows.filter((row) => row.currency.length > 0).map((row) => row.currency)
    : options.map((option) => option.value);
  const addCurrency = options.find((option) => !selectedCurrencies.has(option.value))?.value ?? '';
  const singleCurrencyBandRow = currencyRows[0] ?? emptyCurrencyBandRow(currency);

  function updateRow(index: number, patch: Partial<CurrencyBandFormRow>) {
    const next = currencyRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    setCurrencyRows(next);
    if (provider.multiCurrency && !next.some((row) => row.currency === currency) && next[0]?.currency) {
      setCurrency(next[0].currency);
    }
  }

  if (!provider.multiCurrency && provider.currencyBandFields.length === 0 && pinField === null) {
    return (
      <ConnectionSelect
        provider={provider}
        fieldKey="currency"
        value={currency}
        onChange={setCurrency}
        disabled={disabled}
        marginBottom={compact ? 0 : 2}
      />
    );
  }

  return (
    <Box sx={{ mb: compact ? 1.25 : 2 }}>
      {provider.multiCurrency ? (
        <Box>
          <TextField
            select
            size="small"
            label="Primary currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
            disabled={disabled || readOnly || primaryOptions.length === 0}
            error={Boolean(fieldErrors['currency'])}
            helperText={fieldErrors['currency'] ?? 'Primary currency must have charge bands.'}
            sx={{ minWidth: 220, mb: 2 }}
            slotProps={{ htmlInput: { 'aria-label': 'Primary currency' } }}
          >
            {primaryOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Stack sx={{ gap: 1.25, mb: 1.5 }}>
            {currencyRows.map((row, index) => {
              const rowOptions = options.filter(
                (option) => option.value === row.currency || !selectedCurrencies.has(option.value),
              );
              return (
                <Stack
                  key={`${row.currency}-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  sx={{ gap: 1, alignItems: { sm: 'flex-start' } }}
                >
                  <TextField
                    select
                    size="small"
                    label="Currency"
                    value={row.currency}
                    onChange={(event) => updateRow(index, { currency: event.target.value })}
                    disabled={disabled || readOnly}
                    error={Boolean(fieldErrors[`currencies.${index}.currency`])}
                    helperText={fieldErrors[`currencies.${index}.currency`] ?? undefined}
                    sx={{ minWidth: 150 }}
                    slotProps={{ htmlInput: { 'aria-label': `Currency ${index + 1}` } }}
                  >
                    {rowOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="Min charge"
                    value={row.minMajor}
                    onChange={(event) => updateRow(index, { minMajor: event.target.value })}
                    disabled={disabled || readOnly}
                    error={Boolean(fieldErrors[`currencies.${index}.minChargeMinor`])}
                    helperText={fieldErrors[`currencies.${index}.minChargeMinor`] ?? undefined}
                    slotProps={{ htmlInput: { 'aria-label': `Min charge ${index + 1}`, inputMode: 'decimal' } }}
                  />
                  <TextField
                    size="small"
                    label="Max charge"
                    value={row.maxMajor}
                    onChange={(event) => updateRow(index, { maxMajor: event.target.value })}
                    disabled={disabled || readOnly}
                    error={Boolean(fieldErrors[`currencies.${index}.maxChargeMinor`])}
                    helperText={fieldErrors[`currencies.${index}.maxChargeMinor`] ?? undefined}
                    slotProps={{ htmlInput: { 'aria-label': `Max charge ${index + 1}`, inputMode: 'decimal' } }}
                  />
                  <Button
                    color="inherit"
                    onClick={() => {
                      const next = currencyRows.filter((_, rowIndex) => rowIndex !== index);
                      setCurrencyRows(next);
                      if (!next.some((entry) => entry.currency === currency)) setCurrency(next[0]?.currency ?? '');
                    }}
                    disabled={disabled || readOnly}
                  >
                    Remove
                  </Button>
                </Stack>
              );
            })}
          </Stack>
          {fieldErrors['currencies'] ? (
            <FormHelperText error sx={{ mb: 1 }}>
              {fieldErrors['currencies']}
            </FormHelperText>
          ) : null}
          <Button
            variant="outlined"
            onClick={() => {
              if (addCurrency.length === 0) return;
              const next = [...currencyRows, { currency: addCurrency, minMajor: '', maxMajor: '' }];
              setCurrencyRows(next);
              if (currency.length === 0) setCurrency(addCurrency);
            }}
            disabled={disabled || readOnly || addCurrency.length === 0}
            sx={{ mb: 2 }}
          >
            Add currency
          </Button>
        </Box>
      ) : provider.currencyBandFields.length > 0 ? (
        <Box>
          <Stack
            direction="row"
            sx={{ gap: 1, mb: compact ? 1.25 : 2, alignItems: { sm: 'flex-start' }, flexWrap: 'wrap' }}
            data-testid={compact ? 'payments-connection-settings-main-row' : undefined}
          >
            <ConnectionSelect
              provider={provider}
              fieldKey="currency"
              value={currency}
              onChange={(value) => {
                setCurrency(value);
                setCurrencyRows([{ ...singleCurrencyBandRow, currency: value }]);
              }}
              disabled={disabled || readOnly}
              marginBottom={0}
            />
            <TextField
              size="small"
              label="Min charge"
              value={singleCurrencyBandRow.minMajor}
              onChange={(event) => setCurrencyRows([{ ...singleCurrencyBandRow, minMajor: event.target.value }])}
              disabled={disabled || readOnly}
              error={Boolean(fieldErrors['currencies.0.minChargeMinor'])}
              helperText={fieldErrors['currencies.0.minChargeMinor'] ?? undefined}
              sx={{ minWidth: 150, flex: { sm: '1 1 0' } }}
              slotProps={{ htmlInput: { 'aria-label': 'Min charge 1', inputMode: 'decimal' } }}
            />
            <TextField
              size="small"
              label="Max charge"
              value={singleCurrencyBandRow.maxMajor}
              onChange={(event) => setCurrencyRows([{ ...singleCurrencyBandRow, maxMajor: event.target.value }])}
              disabled={disabled || readOnly}
              error={Boolean(fieldErrors['currencies.0.maxChargeMinor'])}
              helperText={fieldErrors['currencies.0.maxChargeMinor'] ?? undefined}
              sx={{ minWidth: 150, flex: { sm: '1 1 0' } }}
              slotProps={{ htmlInput: { 'aria-label': 'Max charge 1', inputMode: 'decimal' } }}
            />
          </Stack>
          {fieldErrors['currencies'] ? (
            <FormHelperText error sx={{ mb: 1 }}>
              {fieldErrors['currencies']}
            </FormHelperText>
          ) : null}
        </Box>
      ) : (
        <ConnectionSelect
          provider={provider}
          fieldKey="currency"
          value={currency}
          onChange={setCurrency}
          disabled={disabled}
        />
      )}

      {pinField !== null ? (
        <GeneratedField
          field={pinField}
          value={overridePin}
          onChange={setOverridePin}
          saved={descriptor?.overridePinSet === true}
          error={fieldErrors[pinField.key]}
          disabled={disabled || readOnly}
          forcePlainText
          savedPlaceholder="PIN set — enter a new one to change it"
          savedHelperText="Leave blank to keep the current PIN."
          marginBottom={compact ? 0 : 2}
        />
      ) : null}
    </Box>
  );
}

function GeneratedField({
  field,
  value,
  onChange,
  saved,
  error,
  disabled,
  forcePlainText = false,
  savedPlaceholder,
  savedHelperText,
  marginBottom = 2,
}: {
  field: PaymentsFieldDescriptor;
  value: string;
  onChange: (value: string) => void;
  saved: boolean;
  error: string | undefined;
  disabled: boolean;
  forcePlainText?: boolean;
  /** Placeholder while a saved write-only value exists and nothing new is typed. */
  savedPlaceholder?: string;
  savedHelperText?: string;
  marginBottom?: number;
}) {
  if (field.type === 'select') {
    return (
      <TextField
        select
        fullWidth
        size="small"
        label={field.label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error ?? field.helpText ?? undefined}
        sx={{ mb: marginBottom }}
        slotProps={{ htmlInput: { 'aria-label': field.label } }}
      >
        {field.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  const secretField = isSecretField(field);
  const showSavedPlaceholder = secretField && saved && !forcePlainText && value.trim().length === 0;
  return (
    <Box sx={{ mb: marginBottom }}>
      <TextField
        fullWidth
        size="small"
        type={secretField && !forcePlainText ? 'password' : 'text'}
        label={field.label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={
          saved && savedPlaceholder !== undefined && value.trim().length === 0
            ? savedPlaceholder
            : showSavedPlaceholder
              ? SECRET_PLACEHOLDER
              : undefined
        }
        error={Boolean(error)}
        helperText={error ?? (saved && savedHelperText !== undefined ? savedHelperText : field.helpText ?? undefined)}
        autoComplete={secretField && !forcePlainText ? 'new-password' : undefined}
        slotProps={{ htmlInput: { 'aria-label': field.label } }}
      />
      {secretField && saved && savedHelperText === undefined ? (
        <FormHelperText>Empty keeps the saved value. Non-empty rotates it and resets validation.</FormHelperText>
      ) : null}
    </Box>
  );
}

function NotificationUrl({ url }: { url: string | null | undefined }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const [copied, setCopied] = useState(false);
  if (url === null || url === undefined || url.length === 0) return null;
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: t.surface2,
        mb: 2,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.5 }}>Notification URL</Typography>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Typography sx={{ flex: 1, fontFamily: MONO, fontSize: 12.5, wordBreak: 'break-all' }}>
          {url}
        </Typography>
        <Tooltip title={copied ? 'Copied' : 'Copy Notification URL'}>
          <IconButton
            size="small"
            aria-label="Copy Notification URL"
            onClick={() => {
              void navigator.clipboard.writeText(url).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            <Icon name={copied ? 'check' : 'content_copy'} sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function HealthChip({ status, loading }: { status: PaymentsStatus | null; loading: boolean }) {
  if (loading) return <Chip size="small" label="Checking health…" />;
  const overall = status?.connectionStatus.overall ?? 'unconfigured';
  const color = overall === 'healthy' ? 'success' : overall === 'down' ? 'error' : 'warning';
  return <Chip size="small" label={`Health: ${overall}`} color={color} />;
}

function PspLocationPicker({
  pspLocations,
  selected,
  setSelected,
  busy,
  onSave,
}: {
  pspLocations: LoadState<PaymentsPspLocation[]>;
  selected: string;
  setSelected: (value: string) => void;
  busy: boolean;
  onSave: () => void;
}) {
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle sx={{ fontWeight: 500 }}>Choose provider location</AlertTitle>
      {pspLocations.status === 'loading' ? (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <CircularProgress size={16} />
          Loading provider locations…
        </Stack>
      ) : null}
      {pspLocations.status === 'error'
        ? // DNKADM-0440: a 403 reads as access, never as a load failure to retry.
          isForbiddenError(pspLocations.error)
          ? actionErrorMessage(pspLocations.error, 'Couldn’t load provider locations.')
          : errorMessage(pspLocations.error, 'Couldn’t load provider locations.')
        : null}
      {pspLocations.status === 'ready' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, alignItems: 'flex-start', mt: 1 }}>
          <TextField
            select
            size="small"
            label="Provider location"
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
            disabled={busy}
            sx={{ minWidth: 260 }}
            slotProps={{ htmlInput: { 'aria-label': 'Provider location' } }}
          >
            {pspLocations.data.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
                {loc.status !== null ? ` (${loc.status})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={onSave} disabled={busy || selected.length === 0}>
            Save provider location
          </Button>
        </Stack>
      ) : null}
    </Alert>
  );
}
